import React, { useState, useEffect } from 'react';
import { usePersistedState, useStableKeyId } from '@/hooks/usePersistedState';
import { LastEditedBy } from '@/components/shared/LastEditedBy';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Calendar, ChevronLeft, ChevronRight, Plus, Download, Upload,
  Edit2, Trash2, TrendingUp, DollarSign, Target, X, Check,
  Settings, ChevronDown, FileSpreadsheet, FileJson, FileText,
  MoreHorizontal, PlusCircle, Filter, ArrowUpRight, ArrowDownRight, 
  Eye, Users, Hand, CheckCircle, ClipboardList, Building2, Tag, FileQuestion,
  MessageCircle, RefreshCw, Loader2, Radio, Share2, CheckSquare, Info, Sparkles
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Import refactored components
import { SectionHeader } from './daily-data/SectionHeader';
import { PlatformTooltip, calcTotal } from './daily-data/PlatformTooltip';
import QuickLogTemplateModal from './operation/QuickLogTemplateModal';
import { ProviderSelect, ProviderBadge } from './operation/ProviderSelect';
import { providerLabel } from './operation/signalProviders';
import SyncSelectModal from './operation/SyncSelectModal';
import TelegramSyncStatusCard from './operation/TelegramSyncStatusCard';
import {
  platforms,
  monthNames,
  shortMonthNames,
  forexPairs,
  positions,
  tradeStatuses,
  channelOptions,
  defaultBrokerConfig,
  defaultMarketingData,
  defaultSalesData,
  defaultOperationData,
  initialMarketingEntry,
  initialSalesEntry,
  initialOperationEntry,
} from './daily-data/constants';

const API = process.env.REACT_APP_BACKEND_URL;

export default function DailyDataContent({ ib }) {
  const P = `daily-data:${useStableKeyId(ib?.id)}`;
  const [activeTab, setActiveTab] = usePersistedState(`${P}:tab`, 'marketing');
  const [showQuickLogModal, setShowQuickLogModal] = useState(false);
  // Sync & Select flow: user fetches Telegram messages, picks one from list,
  // then Quick Log opens with text prefilled + auto-parsed.
  const [showSyncSelectModal, setShowSyncSelectModal] = useState(false);
  const [prefillQuickLogText, setPrefillQuickLogText] = useState('');
  // Auto-navigate to current month (falls back to Jan for years other than current)
  const [currentMonth, setCurrentMonth] = usePersistedState(`${P}:month`, (() => {
    const now = new Date();
    return now.getFullYear() === 2026 ? now.getMonth() : 0;
  })());
  const [currentYear, setCurrentYear] = useState(2026);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showBrokerConfig, setShowBrokerConfig] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [hoveredCell, setHoveredCell] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [dataImported, setDataImported] = useState(false);
  const dataLoadedRef = React.useRef(false); // Prevent saving before first fetch
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(null);
  
  // USD to MYR exchange rate (auto-fetch from API)
  const [usdToMyr, setUsdToMyr] = useState(() => {
    const saved = localStorage.getItem('usd_myr_rate');
    return saved ? parseFloat(saved) : 4.0; // Default fallback
  });
  const [rateLastUpdated, setRateLastUpdated] = useState(null);
  
  // Fetch live USD/MYR rate
  useEffect(() => {
    const fetchExchangeRate = async () => {
      try {
        // Using exchangerate-api.com (free tier)
        const response = await fetch('https://api.exchangerate-api.com/v4/latest/USD');
        const data = await response.json();
        if (data.rates && data.rates.MYR) {
          const rate = data.rates.MYR;
          setUsdToMyr(rate);
          setRateLastUpdated(new Date().toISOString());
          localStorage.setItem('usd_myr_rate', rate.toString());
          localStorage.setItem('usd_myr_rate_updated', new Date().toISOString());
        }
      } catch (error) {
        console.log('Using cached exchange rate:', usdToMyr);
      }
    };
    
    // Check if rate is stale (older than 1 hour)
    const lastUpdated = localStorage.getItem('usd_myr_rate_updated');
    const isStale = !lastUpdated || (Date.now() - new Date(lastUpdated).getTime()) > 3600000;
    
    if (isStale) {
      fetchExchangeRate();
    } else {
      setRateLastUpdated(lastUpdated);
    }
  }, []);
  
  // Check if FIRA IND
  // Check if IB has imported data (all IBs can have imported data now)
  const hasImportedData = true; // All IBs use API data now
  
  // Get auth headers
  const getHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };
  
  // Broker configuration per IB - loaded from MongoDB
  const [brokerConfig, setBrokerConfig] = useState(defaultBrokerConfig);
  const [brokerConfigLoaded, setBrokerConfigLoaded] = useState(false);
  const [editMeta, setEditMeta] = useState({});
  const [brokerHistory, setBrokerHistory] = useState([]);
  const [slotUsage, setSlotUsage] = useState([]);
  const [brokerKeys, setBrokerKeys] = useState([]);
  const [configSnapshot, setConfigSnapshot] = useState(null);
  const brokerSaveSkipRef = React.useRef(false);
  
  // Load broker history (every broker that ever had data in this IB)
  useEffect(() => {
    if (!ib?.id) return;
    const fetchHistory = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(
          `${API}/api/ib-data/${ib.id}/broker-history`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setBrokerHistory(res.data?.brokers || []);
      } catch (e) {
        console.error('Failed to load broker history:', e);
      }
      try {
        const token = localStorage.getItem('token');
        const res2 = await axios.get(
          `${API}/api/ib-data/${ib.id}/broker-slot-usage`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSlotUsage(res2.data?.slots || []);
        setBrokerKeys(res2.data?.broker_keys || []);
      } catch (e) {
        console.error('Failed to load broker slot usage:', e);
      }
    };
    fetchHistory();
  }, [ib?.id, showBrokerConfig]);
  
  // Load broker config from MongoDB
  useEffect(() => {
    if (!ib?.id) return;
    const fetchBrokerConfig = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/api/ib-data/${ib.id}/broker_config`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.data?.data?.brokerConfig) {
          // User has saved config — use it as-is (never overwrite)
          setBrokerConfig(res.data.data.brokerConfig);
        } else if (ib.active_brokers && ib.active_brokers.length > 0) {
          // No saved config in DB — set defaults from IB's detected brokers
          const brokerNameMap = {
            'octa': 'Octa', 'hfm': 'HFM', 'vantage': 'Vantage',
            'pepperstone': 'Pepperstone', 'moneta': 'Moneta',
            'valetax': 'Valetax', 'monaxa': 'Monaxa'
          };
          const newConfig = {
            brokerA: { name: '', enabled: false },
            brokerB: { name: '', enabled: false },
            brokerC: { name: '', enabled: false },
            brokerD: { name: '', enabled: false },
            brokerE: { name: '', enabled: false },
          };
          const brokerKeys = ['brokerA', 'brokerB', 'brokerC', 'brokerD', 'brokerE'];
          ib.active_brokers.forEach((broker, index) => {
            if (index < 5) {
              newConfig[brokerKeys[index]] = {
                name: brokerNameMap[broker] || broker,
                enabled: true
              };
            }
          });
          setBrokerConfig(newConfig);
        }
        if (res.data?.updated_by_name) {
          setEditMeta({ name: res.data.updated_by_name, time: res.data.updated_at });
        }
        setBrokerConfigLoaded(true);
      } catch (e) {
        console.error('Failed to load broker config:', e);
        setBrokerConfigLoaded(true); // never leave Daily Data blocked on a failed config fetch
      }
    };
    fetchBrokerConfig();
  }, [ib?.id]);
  
  // Save broker config to MongoDB when changed - debounced
  const brokerSaveRef = React.useRef(null);  useEffect(() => {
    if (!ib?.id || !brokerConfigLoaded) return;
    // While the Configure Brokers dialog is open, nothing is persisted: the save
    // goes through handleBrokerConfigSave() so a slot rename always passes the
    // confirm gate instead of being written silently on every keystroke.
    if (showBrokerConfig) return;
    if (brokerSaveSkipRef.current) { brokerSaveSkipRef.current = false; return; }
    if (brokerSaveRef.current) clearTimeout(brokerSaveRef.current);
    brokerSaveRef.current = setTimeout(async () => {
      const token = localStorage.getItem('token');
      try {
        await axios.put(
          `${API}/api/ib-data/${ib.id}/broker_config`,
          { data: { brokerConfig } },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (e) {
        const detail = e?.response?.data?.detail;
        if (detail && typeof detail === 'string') {
          toast.error(`Konfigurasi tidak sah: ${detail}`);
        } else {
          console.error("Auto-save failed:", e);
        }
      }
      window.dispatchEvent(new Event('brokerConfigUpdated'));
    }, 500);
  }, [brokerConfig, ib?.id, brokerConfigLoaded, showBrokerConfig]);

  // State for data - initialized empty, loaded from API or localStorage via useEffect
  const [marketingData, setMarketingData] = useState([]);
  const [salesData, setSalesData] = useState([]);
  const [operationData, setOperationData] = useState([]);

  // Bulk-select state — Sets of row references. Auto-clears when tab/data changes.
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedMkt, setSelectedMkt] = useState(() => new Set());
  const [selectedSales, setSelectedSales] = useState(() => new Set());
  const [selectedOps, setSelectedOps] = useState(() => new Set()); // operation: date keys
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [bulkProvider, setBulkProvider] = useState('');
  const [bulkOnlyUnset, setBulkOnlyUnset] = useState(true);
  const [settingProvider, setSettingProvider] = useState(false);

  const handleBulkSetProvider = async () => {
    if (!bulkProvider || selectedOps.size === 0) return;
    setSettingProvider(true);
    try {
      const dates = [...selectedOps];
      const r = await axios.post(`${API}/api/operation-data/${ib.id}/set-provider`, { dates, provider: bulkProvider, only_unset: bulkOnlyUnset }, { headers: getHeaders() });
      setOperationData((prev) => prev.map((d) => (selectedOps.has(d.date)
        ? { ...d, trades: d.trades.map((t) => (!bulkOnlyUnset || !t.signal_provider ? { ...t, signal_provider: bulkProvider } : t)) }
        : d)));
      toast.success(`${r.data.updated} trade ditetapkan kepada ${bulkProvider}`);
      logEditAction('EDIT', 'Operation', `Set signal provider ${bulkProvider} untuk ${dates.length} hari`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal set signal provider');
    } finally {
      setSettingProvider(false);
    }
  };

  // Clear selections + exit bulk mode when active tab or data refreshes
  React.useEffect(() => {
    setBulkMode(false);
    setSelectedMkt(new Set());
    setSelectedSales(new Set());
    setSelectedOps(new Set());
  }, [activeTab, currentMonth, currentYear, ib?.id]);

  // Bulk delete — splices selected rows from local state + persists via save-manual
  const handleBulkDelete = async (type) => {
    if (type === 'marketing' && selectedMkt.size === 0) return;
    if (type === 'sales' && selectedSales.size === 0) return;
    if (type === 'operation' && selectedOps.size === 0) return;

    const count =
      type === 'marketing' ? selectedMkt.size
      : type === 'sales' ? selectedSales.size
      : selectedOps.size;

    if (!window.confirm(`Delete ${count} selected ${type} ${count > 1 ? 'entries' : 'entry'}?\n\nThis action cannot be undone.`)) return;

    setIsBulkDeleting(true);
    try {
      let newMkt = marketingData, newSales = salesData, newOps = operationData;
      if (type === 'marketing') {
        newMkt = marketingData.filter((r) => !selectedMkt.has(r));
        setMarketingData(newMkt);
        setSelectedMkt(new Set());
      } else if (type === 'sales') {
        newSales = salesData.filter((r) => !selectedSales.has(r));
        setSalesData(newSales);
        setSelectedSales(new Set());
      } else if (type === 'operation') {
        // operationData rows are { date, trades: [...] } — selectedOps holds date keys
        newOps = operationData.filter((d) => !selectedOps.has(d.date));
        setOperationData(newOps);
        setSelectedOps(new Set());
      }
      await saveCurrentData(newMkt, newSales, newOps, true);  // confirmEmpty=true (explicit bulk delete)
      toast.success(`Deleted ${count} ${type} entries`);
    } catch (err) {
      toast.error(`Bulk delete failed: ${err.message || err}`);
    } finally {
      setIsBulkDeleting(false);
    }
  };

  // Toggle helpers
  const toggleSelect = (setter, currentSet, item) => {
    const newSet = new Set(currentSet);
    if (newSet.has(item)) newSet.delete(item); else newSet.add(item);
    setter(newSet);
  };
  const toggleSelectAll = (setter, currentSet, allItems) => {
    if (currentSet.size === allItems.length && allItems.length > 0) {
      setter(new Set());
    } else {
      setter(new Set(allItems));
    }
  };
  
  // Fetch imported data from API for all IBs
  const fetchImportedData = async () => {
    if (!hasImportedData || !ib?.id) return;
    
    setIsLoadingData(true);
    try {
      // Build month key from current selection (e.g., "2026-01" for January 2026)
      const monthKey = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}`;
      const response = await axios.get(`${API}/api/historical/2026/daily-data/${ib.id}?month=${monthKey}`, {
        headers: getHeaders()
      });
      
      const data = response.data;
      const hasData = (data.marketing && data.marketing.length > 0) || (data.sales && data.sales.length > 0);
      
      if (hasData) {
        setMarketingData(data.marketing || []);
        
        // Transform sales data: map the `brokers` dict onto the brokerA..E columns
        // using THIS channel's broker config (slot → name). Using ib.active_brokers
        // (index-based) made the columns show another broker's numbers.
        const brokerKeys = ['brokerA', 'brokerB', 'brokerC', 'brokerD', 'brokerE'];
        const slotNames = {};
        brokerKeys.forEach((k) => {
          const nm = (brokerConfig?.[k]?.name || '').trim().toLowerCase();
          if (nm) slotNames[k] = nm;
        });
        const fallbackBrokers = ib.active_brokers || ['octa', 'hfm', 'vantage'];

        const transformedSales = (data.sales || []).map(sale => {
          const transformed = { ...sale };

          // Only remap if the new `brokers` dict format exists
          if (sale.brokers && typeof sale.brokers === 'object') {
            const useConfig = Object.keys(slotNames).length > 0;
            const pairs = useConfig
              ? Object.entries(slotNames)
              : fallbackBrokers.slice(0, 5).map((nm, i) => [brokerKeys[i], String(nm).toLowerCase()]);

            const num = (v) => (typeof v === 'number' ? v : parseFloat(v) || 0);
            const rowVals = (bd) => ({
              active: num(bd.active),
              lots: num(bd.lots),
              commission: num(bd.commission_usd ?? bd.commission),
              deposit: num(bd.deposit_usd ?? bd.deposit),
            });

            // The dict is keyed by REAL broker name, so it is the source of truth
            // for a column. Legacy brokerA..E slots are only a fallback for a
            // broker missing from the dict — and only when that slot is not just
            // a copy of another broker's numbers (production had brokerB holding
            // HFM's values, which made Pepperstone and HFM show identical rows).
            const dictUsed = [];
            pairs.forEach(([slot, name]) => {
              const bd = sale.brokers[name];
              if (bd && typeof bd === 'object') {
                const v = rowVals(bd);
                dictUsed.push(v);
                transformed[slot] = v;
              } else {
                transformed[slot] = { active: 0, lots: 0, commission: 0, deposit: 0 };
              }
            });
            pairs.forEach(([slot, name]) => {
              if (sale.brokers[name]) return;
              const legacy = sale[slot];
              if (!legacy || typeof legacy !== 'object') return;
              const v = rowVals(legacy);
              const duplicate = dictUsed.some((d) => d.commission === v.commission && d.lots === v.lots);
              if (!duplicate) transformed[slot] = v;
            });
          }
          // If no brokers dict, keep legacy brokerA/B/C as-is

          return transformed;
        });
        
        setSalesData(transformedSales);
        setDataImported(true);
        // Save to localStorage for offline access
        localStorage.setItem(`marketing_data_${ib.id}_${monthKey}`, JSON.stringify(data.marketing || []));
        localStorage.setItem(`sales_data_${ib.id}_${monthKey}`, JSON.stringify(transformedSales));
      } else {
        // API returned no data - clear localStorage to prevent stale data reappearing
        localStorage.removeItem(`marketing_data_${ib.id}_${monthKey}`);
        localStorage.removeItem(`sales_data_${ib.id}_${monthKey}`);
        setMarketingData([]);
        setSalesData([]);
      }
      
      // Broker config is now handled entirely by fetchBrokerConfig useEffect
      // Do NOT touch brokerConfig here to avoid race conditions
      dataLoadedRef.current = true; // Mark data as loaded - safe to save now
    } catch (error) {
      console.error('Failed to fetch imported data:', error);
      // Do NOT load stale localStorage on error — that mixes data from other months.
      // Keep state as-is; user will see loading/empty state and can refresh.
      // Important: DON'T mark dataLoadedRef=true here, otherwise saves could overwrite
      // server data with stale state during transient API failure.
    } finally {
      setIsLoadingData(false);
    }
  };
  
  // Import fresh data from Google Sheet
  const importFromGoogleSheet = async () => {
    if (!hasImportedData || !ib?.id) return;
    
    setIsLoadingData(true);
    try {
      const response = await axios.post(`${API}/api/historical/2026/import-daily-data/${ib.id}`, {}, {
        headers: getHeaders()
      });
      
      if (response.data.success) {
        // Refresh data from API after import
        await fetchImportedData();
        toast.success(`Data 2026 berjaya diimport! Marketing: ${response.data.marketing_count}, Sales: ${response.data.sales_count}, Brokers: ${response.data.detected_brokers?.join(', ') || 'N/A'}`);
      }
    } catch (error) {
      console.error('Failed to import data:', error);
      toast.error('Gagal import data dari Google Sheet');
    } finally {
      setIsLoadingData(false);
    }
  };
  
  // Load data on mount and when month/year changes - ALWAYS from MongoDB.
  // Waits for brokerConfigLoaded: the dict → brokerA..E mapping needs THIS
  // channel's real slot names. Running earlier used the default config (where
  // slot B is HFM) and made MBE's Pepperstone column show HFM's numbers.
  useEffect(() => {
    if (!brokerConfigLoaded) return;
    dataLoadedRef.current = false; // Reset on IB/month change — prevent stale saves
    if (hasImportedData) {
      fetchImportedData();
      fetchOperationData(); // Also refresh operation data on month change
    } else {
      // Load from MongoDB API for ALL IBs (not just Google Sheet IBs)
      const fetchManualData = async () => {
        const mk = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}`;
        try {
          const response = await axios.get(`${API}/api/historical/2026/daily-data/${ib.id}?month=${mk}`, {
            headers: getHeaders()
          });
          if (response.data?.marketing?.length > 0 || response.data?.sales?.length > 0) {
            if (response.data.marketing?.length > 0) setMarketingData(response.data.marketing);
            else setMarketingData(defaultMarketingData);
            if (response.data.sales?.length > 0) setSalesData(response.data.sales);
            else setSalesData(defaultSalesData);
          } else {
            setMarketingData(defaultMarketingData);
            setSalesData(defaultSalesData);
          }
          // Load operation data
          const opRes = await axios.get(`${API}/api/operation-data/${ib.id}`, { headers: getHeaders() }).catch(() => null);
          if (opRes?.data?.data?.length > 0) setOperationData(opRes.data.data);
          else setOperationData(defaultOperationData);
          dataLoadedRef.current = true; // Mark loaded
        } catch (e) {
          console.error('Failed to load manual data:', e);
          setMarketingData(defaultMarketingData);
          setSalesData(defaultSalesData);
          setOperationData(defaultOperationData);
        }
      };
      if (ib?.id) fetchManualData();
    }
  }, [ib?.id, currentMonth, currentYear, brokerConfigLoaded]);

  // Save to MongoDB - ONLY called explicitly after user actions, NOT auto-save
  const monthKey = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}`;
  
  const saveToMongoDB = React.useCallback(async (mData, sData, oData, confirmEmpty = false) => {
    if (!ib?.id) return;
    try {
      await axios.put(`${API}/api/daily-data/${ib.id}/save-manual`, {
        month: monthKey,
        marketing: mData,
        sales: sData,
        operation: oData,
        confirm_empty: confirmEmpty,
      }, { headers: getHeaders() });
    } catch (e) {
      console.error('Save failed:', e);
      toast.error('Gagal simpan data');
    }
  }, [ib?.id, monthKey]);
  
  // Helper: save current state to MongoDB — with guard to prevent saving empty/stale data
  const saveCurrentData = React.useCallback((mData, sData, oData, confirmEmpty = false) => {
    if (!ib?.id || !dataLoadedRef.current) return;
    // Extra guard: don't save if both arrays are empty AND not explicitly confirmed (stale state during month switch)
    if (!confirmEmpty && (!mData || mData.length === 0) && (!sData || sData.length === 0) && (!oData || oData.length === 0)) {
      console.warn('Blocked save: all arrays empty — likely stale state');
      return;
    }
    saveToMongoDB(mData, sData, oData, confirmEmpty);
  }, [ib?.id, saveToMongoDB]);

  // Sync operation data to MongoDB
  const syncOperationDataToMongoDB = async () => {
    if (!dataLoadedRef.current) return; // Same guard as saveToMongoDB
    try {
      await axios.post(`${API}/api/operation-data/save`, {
        ib_id: ib.id,
        data: operationData.map(day => ({
          date: day.date,
          trades: day.trades.map(t => ({
            id: t.id,
            pair: t.pair,
            position: t.position,
            status: t.status,
            pips: t.pips,
            channel: t.channel || 'both',
            signal_generated: t.signal_generated || ''
          }))
        }))
      }, {
        headers: getHeaders()
      });
    } catch (error) {
      console.error('Failed to sync operation data:', error);
    }
  };

  // Fetch operation data from MongoDB on mount
  const fetchOperationData = async () => {
    if (!ib?.id) return;
    
    try {
      const response = await axios.get(`${API}/api/operation-data/${ib.id}`, {
        headers: getHeaders()
      });
      
      if (response.data?.success && response.data.data?.length > 0) {
        // Convert MongoDB format back to frontend format
        const formattedData = response.data.data.map(day => ({
          date: day.date,
          trades: day.trades || []
        }));
        setOperationData(formattedData);
      }
    } catch (error) {
      console.error('Failed to fetch operation data:', error);
    }
  };

  // Load operation data from MongoDB on mount
  useEffect(() => {
    if (ib?.id) {
      fetchOperationData();
    }
  }, [ib?.id]);

  // Navigation
  const goToPreviousMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(y => y - 1);
    } else {
      setCurrentMonth(m => m - 1);
    }
  };

  const goToNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(y => y + 1);
    } else {
      setCurrentMonth(m => m + 1);
    }
  };

  // Filter data by month
  const filterByMonth = (data) => {
    return data.filter(item => {
      const date = new Date(item.date);
      return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
    });
  };

  // Export functions
  const handleExport = (format) => {
    const { exportToExcel, exportToPDF } = require('@/utils/export');
    let data, filename, tabName;
    
    if (activeTab === 'marketing') {
      data = filterByMonth(marketingData);
      tabName = 'Marketing';
      filename = `${ib?.name || 'IB'}_Marketing_${currentYear}_${shortMonthNames[currentMonth]}`;
    } else if (activeTab === 'sales') {
      data = filterByMonth(salesData);
      tabName = 'Sales';
      filename = `${ib?.name || 'IB'}_Sales_${currentYear}_${shortMonthNames[currentMonth]}`;
    } else {
      data = operationData.filter(d => {
        const date = new Date(d.date);
        return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
      });
      tabName = 'Operation';
      filename = `${ib?.name || 'IB'}_Operation_${currentYear}_${shortMonthNames[currentMonth]}`;
    }

    if (format === 'excel') {
      // Flatten data for Excel
      const flat = data.map(d => {
        const row = { Date: d.date };
        if (activeTab === 'marketing') {
          if (d.spend) Object.entries(d.spend).forEach(([k,v]) => { row[`Spend ${k.toUpperCase()}`] = v; });
          row['Leads'] = d.leads || 0;
          row['LPV'] = d.lpv || 0;
          row['CTA'] = d.cta || 0;
          row['Subs FT'] = d.subsFreeTrial || 0;
          row['Subs TG'] = d.subsTG || 0;
          row['Subs Bot'] = d.subsBot || 0;
          const totalSubs = (d.subsTG || 0) + (d.subsBot || 0);
          const totalSpend = Object.values(d.spend || {}).reduce((t, v) => t + (v || 0), 0);
          row['CPS'] = totalSubs > 0 ? (totalSpend / totalSubs).toFixed(2) : 0;
          row['CPS (FT)'] = (d.subsFreeTrial || 0) > 0 ? (totalSpend / d.subsFreeTrial).toFixed(2) : 0;
        } else if (activeTab === 'sales') {
          row['Total Lead'] = d.totalLead || d.total_lead || 0;
          row['New Closed'] = d.newClosed || d.closed || 0;
          for (const bk of ['brokerA','brokerB','brokerC','brokerD','brokerE']) {
            if (d[bk]?.active) { row[`${bk} Active`] = d[bk].active; row[`${bk} Lots`] = d[bk].lots; row[`${bk} Comm`] = d[bk].commission; }
          }
        } else {
          const trades = d.trades || [];
          const wins = trades.filter(t => t.status === 'WIN' || t.status === 'BREAKEVEN').length;
          row['Trades'] = trades.length;
          row['Win'] = wins;
          row['Loss'] = trades.filter(t => t.status === 'LOSS').length;
          row['Win Rate'] = trades.length > 0 ? ((wins / trades.length) * 100).toFixed(1) + '%' : '0.0%';
          row['Total Pips'] = trades.reduce((s, t) => s + (t.pips || 0), 0);
          (d.trades || []).forEach((t, i) => { row[`Trade${i+1} Pair`] = t.pair; row[`Trade${i+1} Status`] = t.status; row[`Trade${i+1} Pips`] = t.pips; });
        }
        return row;
      });
      exportToExcel(flat, tabName, filename);
      toast.success(`Exported ${data.length} rows to Excel`);
    } else if (format === 'pdf') {
      const columns = activeTab === 'marketing' ? ['Date', 'FB Spend', 'Leads', 'LPV', 'CTA', 'Subs'] :
                      activeTab === 'sales' ? ['Date', 'Total Lead', 'New Closed', 'Returning Closed', 'Total Closed', 'Closing Rate'] :
                      ['Date', 'Trades', 'Win', 'Loss', 'Win Rate', 'Pips'];
      const rows = data.map(d => {
        if (activeTab === 'marketing') return [d.date, d.spend?.fb || 0, d.leads || 0, d.lpv || 0, d.cta || 0, (d.subsTG || 0) + (d.subsBot || 0)];
        if (activeTab === 'sales') {
          const totalClosed = (d.newClosed || d.closed || 0) + (d.vipClosed || 0);
          const totalLeads = d.totalLead || d.leads || 0;
          const closingRate = totalLeads > 0 ? ((totalClosed / totalLeads) * 100).toFixed(1) + '%' : '0.0%';
          return [d.date, totalLeads, d.newClosed || 0, d.vipClosed || 0, totalClosed, closingRate];
        }
        const trades = d.trades || [];
        const wins = trades.filter(t=>t.status==='WIN'||t.status==='BREAKEVEN').length;
        const winRate = trades.length > 0 ? ((wins / trades.length) * 100).toFixed(1) + '%' : '0.0%';
        return [d.date, trades.length, wins, trades.filter(t=>t.status==='LOSS').length, winRate, trades.reduce((s,t)=>s+(t.pips||0),0)];
      });
      exportToPDF(`${ib?.name} — ${tabName} Daily Data`, `${shortMonthNames[currentMonth]} ${currentYear}`, columns, rows, filename);
      toast.success(`Exported to PDF`);
    } else if (format === 'json') {
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      downloadBlob(blob, `${filename}.json`);
    } else if (format === 'csv') {
      // Simplified CSV export
      const csv = JSON.stringify(data);
      const blob = new Blob([csv], { type: 'text/csv' });
      downloadBlob(blob, `${filename}.csv`);
    }
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Import file handler
  const handleImportFile = async () => {
    if (!importFile || !ib?.id) {
      toast.error('Sila pilih fail untuk import');
      return;
    }
    
    setIsImporting(true);
    setImportProgress('Memproses fail...');
    
    const formData = new FormData();
    formData.append('file', importFile);
    
    try {
      const response = await axios.post(
        `${API}/api/daily-data/import-file/${ib.id}`,
        formData,
        {
          headers: {
            ...getHeaders(),
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      
      if (response.data.success) {
        const result = response.data;
        let msg = 'Import berjaya! ';
        if (result.marketing_count > 0) msg += `Marketing: ${result.marketing_count} records. `;
        if (result.sales_count > 0) msg += `Sales: ${result.sales_count} records. `;
        if (result.operation_count > 0) msg += `Operation: ${result.operation_count} trades. `;
        if (result.detected_brokers?.length > 0) {
          msg += `Broker: ${result.detected_brokers.join(', ')}. `;
        }
        // Warn if multiple months touched — user may need to navigate to see all data
        const touched = result.months_touched || [];
        if (touched.length > 1) {
          const currentMK = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}`;
          const otherMonths = touched.filter(m => m !== currentMK);
          if (otherMonths.length > 0) {
            msg += `\n\nNota: Data juga tersimpan untuk bulan: ${otherMonths.join(', ')}. Navigate ke bulan tersebut untuk lihat.`;
          }
        }
        // Surface auto-added brokers so user knows new column appears
        if (result.auto_added_brokers?.length > 0) {
          msg += `\n\nBroker baru ditambah ke Configure: ${result.auto_added_brokers.join(', ')}`;
        }
        toast.success(msg);
        
        // Refresh broker config so the new column appears immediately
        if (result.auto_added_brokers?.length > 0) {
          try {
            const bcRes = await axios.get(`${API}/api/ib-data/${ib.id}/broker_config`, { headers: getHeaders() });
            if (bcRes.data?.data?.brokerConfig) {
              setBrokerConfig(bcRes.data.data.brokerConfig);
            }
            window.dispatchEvent(new Event('brokerConfigUpdated'));
          } catch (e) {
            console.warn('Failed to refresh broker config:', e);
          }
        }
        
        // Refresh data
        await fetchImportedData();
        
        setShowImportModal(false);
        setImportFile(null);
      } else {
        toast.error('Import gagal: ' + (response.data.detail || 'Unknown error'));
      }
    } catch (error) {
      console.error('Import error:', error);
      const msg = error.response?.data?.detail || error.message || 'Import gagal';
      toast.error(msg);
    } finally {
      setIsImporting(false);
      setImportProgress(null);
    }
  };

  // Download template
  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/api/daily-data/template`, {
        headers: getHeaders()
      });
      
      if (response.data) {
        // Create CSV template based on active tab
        let headers = [];
        if (activeTab === 'marketing') {
          headers = ['DATE', ...response.data.marketing.optional];
        } else if (activeTab === 'sales') {
          headers = ['DATE', ...response.data.sales.optional];
        } else {
          headers = [...response.data.operation.required, ...response.data.operation.optional];
        }
        
        const csv = headers.join(',') + '\n';
        const blob = new Blob([csv], { type: 'text/csv' });
        downloadBlob(blob, `template_${activeTab}.csv`);
        toast.success('Template downloaded!');
      }
    } catch (error) {
      toast.error('Gagal download template');
    }
  };

  // New entry form state
  const [newMarketingEntry, setNewMarketingEntry] = useState({
    date: '',
    spend: { fb: 0, ig: 0, tt: 0, tg: 0, gg: 0, yt: 0 },
    impressions: { fb: 0, ig: 0, tt: 0, tg: 0, gg: 0, yt: 0 },
    clicks: { fb: 0, ig: 0, tt: 0, tg: 0, gg: 0, yt: 0 },
    lpv: 0, cta: 0, subsFreeTrial: 0, subsTG: 0, subsBot: 0, leads: 0, closed: 0,
  });

  const [newSalesEntry, setNewSalesEntry] = useState({
    date: '',
    leadsM: { new: 0, prospect: 0, vip: 0 },
    leadsS: { new: 0, prospect: 0, vip: 0 },
    leadsIB: { new: 0, prospect: 0, vip: 0 },
    newClosed: 0,
    vipClosed: 0,
    brokerA: { active: 0, lots: 0, commission: 0, deposit: 0 },
    brokerB: { active: 0, lots: 0, commission: 0, deposit: 0 },
    brokerC: { active: 0, lots: 0, commission: 0, deposit: 0 },
    brokerD: { active: 0, lots: 0, commission: 0, deposit: 0 },
    brokerE: { active: 0, lots: 0, commission: 0, deposit: 0 },
    greeting: 0, oneTick: 0, registration: 0, broker: 0, priceOffer: 0, others: 0,
  });

  const [newOperationEntry, setNewOperationEntry] = useState({
    date: '',
    pair: 'XAUUSD',
    position: 'BUY',
    status: 'WIN',
    pips: 0,
    channel: 'both',
    signal_generated: '',
    signal_provider: '',
  });

  // Log specific action to edit history
  const logEditAction = async (action, tab, details) => {
    try {
      await axios.post(`${API}/api/edit-history`, {
        resource_type: 'daily-data',
        resource_id: ib?.id,
        page: 'Daily Data',
        section: tab,
        action: action,
        changes: [{ field: tab, detail: details }]
      }, { headers: getHeaders() });
    } catch {
      /* activity log is best-effort */
    }
  };

  // Share daily data as formatted text (copy to clipboard for WhatsApp/Telegram)
  const handleShareRow = (row, tab) => {
    const date = new Date(row.date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' });
    const channelName = ib?.name || 'Channel';
    let text = '';
    
    if (tab === 'marketing') {
      const totalSpend = calcTotal(row.spend);
      const totalImpressions = calcTotal(row.impressions);
      const totalClicks = calcTotal(row.clicks);
      const subsFT = row.subsFreeTrial || 0;
      const totalSubs = (row.subsTG || 0) + (row.subsBot || 0);
      const cps = totalSubs > 0 ? (totalSpend / totalSubs).toFixed(2) : 'N/A';
      const cpsFT = subsFT > 0 ? (totalSpend / subsFT).toFixed(2) : 'N/A';
      
      text = `*Daily Target / KPI ${date} ${channelName}*\n\n`;
      text += `*Marketing*\n`;
      text += `Total Spend: RM ${totalSpend.toLocaleString()}\n`;
      text += `Impressions: ${totalImpressions.toLocaleString()}\n`;
      text += `Clicks: ${totalClicks.toLocaleString()}\n`;
      text += `LPV: ${(row.lpv || 0).toLocaleString()}\n`;
      text += `CTA: ${(row.cta || 0).toLocaleString()}\n`;
      text += `Subs FT: ${subsFT.toLocaleString()}\n`;
      text += `Subs TG: ${(row.subsTG || 0).toLocaleString()}\n`;
      text += `Subs Bot: ${(row.subsBot || 0).toLocaleString()}\n`;
      text += `Total Subs: ${totalSubs.toLocaleString()}\n`;
      text += `CPS: RM ${cps}\n`;
      text += `CPS (FT): RM ${cpsFT}\n`;
    } else if (tab === 'sales') {
      const totalLeads = (row.leadsM?.new || 0) + (row.leadsM?.prospect || 0) + (row.leadsM?.vip || 0) +
                          (row.leadsS?.new || 0) + (row.leadsS?.prospect || 0) + (row.leadsS?.vip || 0) +
                          (row.leadsIB?.new || 0) + (row.leadsIB?.prospect || 0) + (row.leadsIB?.vip || 0) || row.leads || 0;
      const newClosed = row.newClosed || row.closed || 0;
      const returningClosed = row.vipClosed || 0;
      const closingRate = totalLeads > 0 ? ((newClosed / totalLeads) * 100).toFixed(1) : '0.0';
      
      text = `*💼 SALES — ${date}*\n*${channelName}*\n\n`;

      // Sales Pipeline
      text += `*Sales Pipeline*\n`;
      text += `Greeting: ${row.greeting || 0}\n`;
      text += `1 tick : ${row.oneTick || 0}\n`;
      text += `Reg : ${row.registration || 0}\n`;
      text += `Broker : ${row.broker || 0}\n`;
      text += `Price : ${row.priceOffer || 0}\n`;
      text += `Others: ${row.others || 0}\n`;

      // Sales Conversion
      text += `\n*Sales Conversion*\n`;
      text += `New Leads: ${totalLeads}\n`;
      text += `Close Client: ${newClosed}\n`;
      text += `Returning Client: ${returningClosed}\n`;
      text += `Closing Rate: ${closingRate}%\n`;
      
      // Broker breakdown — use brokers dict (real names) or brokerA/B/C
      let totalActive = 0, totalLots = 0, totalComm = 0, totalDep = 0;
      if (row.brokers && typeof row.brokers === 'object') {
        Object.entries(row.brokers).forEach(([bn, bd]) => {
          if (bd && typeof bd === 'object') {
            const name = (bd.name || bn).toUpperCase();
            const a = bd.active || 0;
            const l = bd.lots || 0;
            const c = bd.commission_usd || bd.commission || 0;
            const d = bd.deposit_usd || bd.deposit || 0;
            text += `\n*${name}*\n`;
            text += `Active Client: ${a}\n`;
            text += `Traded Lot: ${l} lot\n`;
            text += `Commission: $${c.toLocaleString(undefined, {minimumFractionDigits:2, maximumFractionDigits:2})} USD\n`;
            text += `Com/Lot: ${l > 0 ? '$' + (c / l).toFixed(2) : '-'}\n`;
            text += `Deposit : $${d.toLocaleString(undefined, {minimumFractionDigits:2, maximumFractionDigits:2})}\n`;
            totalActive += a; totalLots += l; totalComm += c; totalDep += d;
          }
        });
      } else {
        enabledBrokers.forEach(([key, config]) => {
          const bd = row[key] || {};
          if (bd.active || bd.lots || bd.commission) {
            text += `\n*${config.name}*\n`;
            text += `Active Client: ${bd.active || 0}\n`;
            text += `Traded Lot: ${bd.lots || 0} lot\n`;
            text += `Commission: $${(bd.commission || 0).toLocaleString(undefined, {minimumFractionDigits:2, maximumFractionDigits:2})} USD\n`;
            text += `Com/Lot: ${(bd.lots || 0) > 0 ? '$' + ((bd.commission || 0) / bd.lots).toFixed(2) : '-'}\n`;
            text += `Deposit : $${(bd.deposit || 0).toLocaleString(undefined, {minimumFractionDigits:2, maximumFractionDigits:2})}\n`;
            totalActive += (bd.active || 0); totalLots += (bd.lots || 0); totalComm += (bd.commission || 0); totalDep += (bd.deposit || 0);
          }
        });
      }
      
      // Totals
      text += `\n*Total Commission: $${totalComm.toLocaleString(undefined, {minimumFractionDigits: 2})}*\n`;
      text += `Total Active: ${totalActive} | Total Lots: ${totalLots}\n`;
      text += `Avg Com/Lot: ${totalLots > 0 ? '$' + (totalComm / totalLots).toFixed(2) : '-'}\n`;
      text += `Total Deposit : $${totalDep.toLocaleString(undefined, {minimumFractionDigits: 2})}\n`;
    } else if (tab === 'operation') {
      text = `*Daily Target / KPI ${date} ${channelName}*\n\n`;
      text += `*Operation*\n`;
      const trades = row.trades || [];
      const wins = trades.filter(t => t.status === 'WIN' || t.status === 'BREAKEVEN').length;
      const losses = trades.filter(t => t.status === 'LOSS').length;
      const winRate = trades.length > 0 ? ((wins / trades.length) * 100).toFixed(1) : '0.0';
      text += `Total Setups: ${trades.length}\n`;
      text += `Win: ${wins}\n`;
      text += `Loss: ${losses}\n`;
      text += `Win Rate: ${winRate}%\n`;
      text += `Total Pips: ${trades.reduce((s, t) => s + (t.pips || 0), 0)}\n`;
      const byProvider = trades.reduce((acc, t) => ({ ...acc, [providerLabel(t.signal_provider)]: (acc[providerLabel(t.signal_provider)] || 0) + 1 }), {});
      text += `Signal Provider: ${Object.entries(byProvider).map(([p, n]) => `${p} (${n})`).join(', ')}\n`;
    }
    
    navigator.clipboard.writeText(text).then(() => {
      toast.success('Copied! Paste ke WhatsApp/Telegram', { duration: 3000 });
    }).catch(() => {
      toast.error('Failed to copy');
    });
  };


  // Watchdog: a keystroke slip like active 17,187 instead of 171 used to sail
  // straight into the monthly snapshot. Warn (never block) when a value dwarfs
  // this month's own median for the same broker column.
  const OUTLIER_RULES = { active: 500, lots: 300, commission: 1000, deposit: 50000 };
  const warnIfOutlier = (entry) => {
    const slots = ['brokerA', 'brokerB', 'brokerC', 'brokerD', 'brokerE'];
    const median = (arr) => {
      const v = arr.filter((n) => n > 0).sort((a, b) => a - b);
      if (!v.length) return 0;
      const mid = Math.floor(v.length / 2);
      return v.length % 2 ? v[mid] : (v[mid - 1] + v[mid]) / 2;
    };
    Object.entries(OUTLIER_RULES).forEach(([field, floor]) => {
      const history = [];
      salesData.forEach((row) => slots.forEach((s) => {
        const val = parseFloat(row?.[s]?.[field]) || 0;
        if (val > 0) history.push(val);
      }));
      const med = median(history);
      if (med <= 0) return;
      slots.forEach((s) => {
        const val = parseFloat(entry?.[s]?.[field]) || 0;
        if (val >= floor && val >= med * 10) {
          const name = brokerConfig?.[s]?.name || s;
          toast.warning(
            `Angka pelik: ${field} ${val.toLocaleString()} untuk ${name} — ${Math.round(val / med)}x median bulan ni (${Math.round(med).toLocaleString()}). Sila semak sebelum ia masuk snapshot.`,
            { duration: 9000 }
          );
        }
      });
    });
  };


  // CRUD handlers
  const handleCreateEntry = () => {
    if (activeTab === 'marketing') {
      if (!newMarketingEntry.date) return;
      const entry = { ...newMarketingEntry, id: Date.now() };
      const newMkt = [...marketingData, entry];
      setMarketingData(newMkt);
      saveCurrentData(newMkt, salesData, operationData);
      setNewMarketingEntry({
        date: '', spend: { fb: 0, ig: 0, tt: 0, tg: 0, gg: 0, yt: 0 },
        impressions: { fb: 0, ig: 0, tt: 0, tg: 0, gg: 0, yt: 0 },
        clicks: { fb: 0, ig: 0, tt: 0, tg: 0, gg: 0, yt: 0 },
        lpv: 0, cta: 0, subsFreeTrial: 0, subsTG: 0, subsBot: 0, leads: 0, closed: 0,
      });
    } else if (activeTab === 'sales') {
      if (!newSalesEntry.date) return;
      const entry = { ...newSalesEntry, id: Date.now() };
      warnIfOutlier(entry);
      const newSales = [...salesData, entry];
      setSalesData(newSales);
      saveCurrentData(marketingData, newSales, operationData);
    } else if (activeTab === 'operation') {
      if (!newOperationEntry.date) return;
      if (!newOperationEntry.signal_provider) {
        toast.error('Pilih Signal Provider dahulu');
        return;
      }
      const existingDateIdx = operationData.findIndex(d => d.date === newOperationEntry.date);
      let newOps;
      if (existingDateIdx >= 0) {
        newOps = operationData.map((d, idx) => idx === existingDateIdx ? { ...d, trades: [...d.trades, { id: Date.now(), pair: newOperationEntry.pair, position: newOperationEntry.position, status: newOperationEntry.status, pips: parseFloat(newOperationEntry.pips) || 0, channel: newOperationEntry.channel || 'both', signal_generated: newOperationEntry.signal_generated || '', signal_provider: newOperationEntry.signal_provider }] } : d);
      } else {
        newOps = [...operationData, { date: newOperationEntry.date, trades: [{ id: Date.now(), pair: newOperationEntry.pair, position: newOperationEntry.position, status: newOperationEntry.status, pips: parseFloat(newOperationEntry.pips) || 0, channel: newOperationEntry.channel || 'both', signal_generated: newOperationEntry.signal_generated || '', signal_provider: newOperationEntry.signal_provider }] }];
      }
      setOperationData(newOps);
      saveCurrentData(marketingData, salesData, newOps);
      setNewOperationEntry({ date: '', pair: 'XAUUSD', position: 'BUY', status: 'WIN', pips: 0, channel: 'both', signal_generated: '', signal_provider: '' });
    }
    setShowCreateModal(false);
    // Log ADD action
    const tabName = activeTab === 'marketing' ? 'Marketing' : activeTab === 'sales' ? 'Sales' : 'Operation';
    const dateVal = activeTab === 'marketing' ? newMarketingEntry.date : activeTab === 'sales' ? newSalesEntry.date : newOperationEntry.date;
    logEditAction('ADD', tabName, `Added ${tabName.toLowerCase()} entry for ${dateVal || 'new date'}`);
  };

  // State for delete confirmation and undo
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, date, type, data }
  const [deletedEntries, setDeletedEntries] = useState([]); // Stack of deleted entries for undo
  const [isDeleting, setIsDeleting] = useState(false);

  // Show delete confirmation popup
  const confirmDelete = (id, date, entryData = null) => {
    // Find the full data for undo capability
    let fullData = entryData;
    if (activeTab === 'marketing' && !fullData) {
      fullData = marketingData.find(e => e.id === id);
    } else if (activeTab === 'sales' && !fullData) {
      fullData = salesData.find(e => e.id === id);
    }
    
    setDeleteTarget({ 
      id, 
      date: date || fullData?.date, 
      type: activeTab, 
      data: fullData 
    });
    setShowDeleteConfirm(true);
  };

  // Execute delete with API call
  const handleDeleteEntry = async () => {
    if (!deleteTarget) return;
    
    const { id, date, type, data } = deleteTarget;
    setIsDeleting(true);
    
    try {
      // Update local state and save
      let newMkt = marketingData, newSales = salesData, newOps = operationData;
      if (type === 'marketing') {
        // Index-immune delete: use the row reference (passed as `data`) to find the EXACT row,
        // splice by index. Avoids id-collision bug + skips the buggy date-based $pull endpoint.
        const idx = data ? marketingData.indexOf(data) : -1;
        if (idx >= 0) {
          newMkt = [...marketingData.slice(0, idx), ...marketingData.slice(idx + 1)];
        } else {
          // Fallback: filter by id (legacy path)
          newMkt = marketingData.filter(e => e.id !== id);
        }
        setMarketingData(newMkt);
      } else if (type === 'sales') {
        const idx = data ? salesData.indexOf(data) : -1;
        if (idx >= 0) {
          newSales = [...salesData.slice(0, idx), ...salesData.slice(idx + 1)];
        } else {
          newSales = salesData.filter(e => e.id !== id);
        }
        setSalesData(newSales);
      } else if (type === 'operation') {
        // Operation trades still use the API DELETE endpoint (trade ids ARE unique within a day)
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        await axios.delete(`${API}/api/daily-data/${ib.id}/entry`, {
          headers,
          params: { date: date, entry_type: type, entry_id: id }
        });
        newOps = operationData.map(d => d.date === date ? { ...d, trades: d.trades.filter(t => t.id !== id) } : d).filter(d => d.trades.length > 0);
        setOperationData(newOps);
      }
      // EXPLICIT SAVE after delete (overwrites the full array via /save-manual, eliminating duplicates safely)
      saveCurrentData(newMkt, newSales, newOps);
      
      // Store deleted entry for undo (keep last 5)
      setDeletedEntries(prev => [{
        id,
        date,
        type,
        data,
        deletedAt: new Date().toISOString()
      }, ...prev.slice(0, 4)]);
      
      toast.success('Data berjaya dipadam', {
        description: `${type.charAt(0).toUpperCase() + type.slice(1)} entry pada ${date}`,
        action: {
          label: 'Undo',
          onClick: () => handleUndoDelete({ id, date, type, data })
        }
      });
      
      // Log DELETE action
      const tabName = type.charAt(0).toUpperCase() + type.slice(1);
      logEditAction('DELETE', tabName, `Deleted ${type} entry for ${date}`);
      
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Gagal memadam data', {
        description: error.response?.data?.detail || 'Sila cuba lagi'
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
      setDeleteTarget(null);
    }
  };

  // Undo delete function
  const handleUndoDelete = async (entry) => {
    if (!entry) return;
    
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      await axios.post(`${API}/api/daily-data/${ib.id}/undo-delete`, {
        type: entry.type,
        date: entry.date,
        data: entry.data
      }, { headers });
      
      // Restore to local state
      if (entry.type === 'marketing') {
        setMarketingData(prev => [...prev, entry.data].sort((a, b) => new Date(a.date) - new Date(b.date)));
      } else if (entry.type === 'sales') {
        setSalesData(prev => [...prev, entry.data].sort((a, b) => new Date(a.date) - new Date(b.date)));
      } else if (entry.type === 'operation') {
        setOperationData(prev => {
          const existing = prev.find(d => d.date === entry.date);
          if (existing) {
            return prev.map(d => d.date === entry.date 
              ? { ...d, trades: [...d.trades, entry.data] }
              : d
            );
          }
          return [...prev, { date: entry.date, trades: [entry.data] }].sort((a, b) => new Date(a.date) - new Date(b.date));
        });
      }
      
      // Remove from deleted entries
      setDeletedEntries(prev => prev.filter(e => e.id !== entry.id));
      
      toast.success('Data telah dipulihkan', {
        description: `${entry.type.charAt(0).toUpperCase() + entry.type.slice(1)} entry pada ${entry.date}`
      });
      
    } catch (error) {
      console.error('Undo error:', error);
      toast.error('Gagal memulihkan data', {
        description: error.response?.data?.detail || 'Sila cuba lagi'
      });
    }
  };

  // Get enabled brokers
  const enabledBrokers = Object.entries(brokerConfig).filter(([_, config]) => config.enabled && config.name);

  // Snapshot the config when the dialog opens, so renames can be detected on save.
  useEffect(() => {
    if (showBrokerConfig) setConfigSnapshot(JSON.parse(JSON.stringify(brokerConfig)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showBrokerConfig]);

  // Renaming a slot no longer moves historical data automatically. On save we ask
  // once, explicitly, what should happen — for both name-keyed and legacy slot data.
  const handleBrokerConfigSave = async () => {
    const slots = ['brokerA', 'brokerB', 'brokerC', 'brokerD', 'brokerE'];
    let configToSave = brokerConfig;
    const migrateList = [];

    for (const slot of slots) {
      const oldName = (configSnapshot?.[slot]?.name || '').trim();
      const newName = (brokerConfig?.[slot]?.name || '').trim();
      if (!newName || oldName.toLowerCase() === newName.toLowerCase()) continue;

      const dictData = oldName ? brokerKeys.find(k => k.key === oldName.toLowerCase()) : null;
      const legacyData = slotUsage.find(s => s.slot === slot && s.entry_count > 0);

      // One prompt listing every consequence of this rename, whichever data shape exists.
      const facts = [];
      if (legacyData) {
        facts.push(`• Slot ni ada ${legacyData.entry_count} entries data lama (${legacyData.month_count} bulan · $${legacyData.commission_usd.toLocaleString()}) — data tu akan TERUS dipapar sebagai "${newName}" walaupun team belum key-in apa-apa.`);
      }
      if (dictData) {
        facts.push(`• Nama lama "${oldName}" ada ${dictData.entry_count} entries ($${dictData.commission_usd.toLocaleString()}) yang disimpan ikut nama.`);
      }
      if (facts.length === 0) continue;  // brand new slot with no history — nothing to warn about

      const proceed = window.confirm(
        `⚠️ Slot ${slot.slice(-1)}: "${oldName || '(kosong)'}" → "${newName}"\n\n${facts.join('\n')}\n\n` +
        `OK = teruskan tukar nama.\n` +
        `Cancel = batalkan (untuk broker BARU, guna slot yang betul-betul kosong, atau archive data slot ni dulu di Admin → Broker Cleanup).`
      );
      if (!proceed) {
        configToSave = {
          ...configToSave,
          [slot]: { ...configToSave[slot], name: oldName, enabled: configSnapshot?.[slot]?.enabled ?? false },
        };
        setBrokerConfig(configToSave);
        toast.info(`Tukar nama slot ${slot.slice(-1)} dibatalkan`);
        continue;
      }
      if (dictData) {
        const migrate = window.confirm(
          `Data lama "${oldName}" (${dictData.entry_count} entries, $${dictData.commission_usd.toLocaleString()}):\n\n` +
          `OK = PINDAHKAN ke nama baru "${newName}".\n` +
          `Cancel = KEKALKAN pada nama "${oldName}" (tak disentuh).`
        );
        if (migrate) migrateList.push({ old: oldName.toLowerCase(), new: newName.toLowerCase() });
        else toast.info(`Data "${oldName}" kekal pada nama lama`);
      }
    }

    try {
      const token = localStorage.getItem('token');
      const res = await axios.put(
        `${API}/api/ib-data/${ib.id}/broker_config`,
        { data: { brokerConfig: configToSave }, ...(migrateList.length > 0 ? { migrate_renames: migrateList } : {}) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const migrations = res?.data?.rename_migrations || [];
      toast.success(migrations.length > 0
        ? `Data dipindahkan: ${migrations.map(m => `${m.old} → ${m.new} (${m.docs_touched} bulan)`).join(', ')}`
        : 'Broker config disimpan');
      window.dispatchEvent(new Event('brokerConfigUpdated'));
      if (migrations.length > 0 && hasImportedData) fetchImportedData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal simpan broker config');
      return;
    }
    brokerSaveSkipRef.current = true;  // config already persisted here
    setShowBrokerConfig(false);
  };

  return (
    <>
      {/* Sticky Header - Matching other pages design */}
      <header className="md:sticky md:top-0 z-20 bg-gradient-to-r from-[#09090B]/98 via-[#0a0a0c]/95 to-[#09090B]/98 backdrop-blur-xl border-b border-[hsl(var(--border))]">
        <div className="px-4 sm:px-8 py-3 sm:py-5 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-8 h-8 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-[#E63946] to-[#F97316] flex items-center justify-center shadow-lg shadow-[#E63946]/20 shrink-0">
              <Calendar size={16} className="sm:w-6 sm:h-6 text-white" />
            </div>
            <div>
              <h1 className="text-lg sm:text-2xl font-bold text-white tracking-tight">Daily Data</h1>
              <p className="text-xs sm:text-sm text-[#9CA3AF]">Marketing, Sales & Operations</p>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
            {/* Quick Log from Template — only show on Operation tab */}
            {activeTab === 'operation' && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 hover:border-emerald-500/60"
                  onClick={() => setShowSyncSelectModal(true)}
                  data-testid="sync-select-btn"
                  title="Auto-fetch dari Telegram, review, then save"
                >
                  <Radio size={14} className="mr-2" />
                  Sync &amp; Select
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10 hover:border-amber-500/60"
                  onClick={() => setShowQuickLogModal(true)}
                  data-testid="quick-log-template-btn"
                >
                  <Sparkles size={14} className="mr-2" />
                  Quick Log
                </Button>
              </>
            )}

            {/* Import Button - Always visible */}
            <Button 
              variant="outline" 
              size="sm" 
              className="border-[hsl(var(--border))] text-white hover:bg-[#10B981]/10 hover:border-[#10B981]/50"
              onClick={() => setShowImportModal(true)}
              data-testid="import-file-btn"
            >
              <Upload size={14} className="mr-2" />
              Import Data
            </Button>

            {/* Add Data Button */}
            <Button
              onClick={() => setShowCreateModal(true)}
              className="bg-gradient-to-r from-[#E63946] to-[#F97316] hover:from-[#E63946]/90 hover:to-[#F97316]/90 text-white"
              data-testid="add-daily-data-btn"
            >
              <Plus size={16} className="mr-2" />
              Add Data
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden">
        <div className="px-3 sm:px-8 py-4 sm:py-6 space-y-4 sm:space-y-6">
          
          {/* Department Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
              <TabsList className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] shrink-0">
                <TabsTrigger 
                  value="marketing" 
                  data-testid="dept-tab-marketing"
                  className="data-[state=active]:bg-[#E63946] data-[state=active]:text-white text-xs sm:text-sm"
                >
                  <TrendingUp size={12} className="mr-1 sm:mr-2" />
                  Marketing
                </TabsTrigger>
                <TabsTrigger 
                  value="sales" 
                  data-testid="dept-tab-sales"
                  className="data-[state=active]:bg-[#10B981] data-[state=active]:text-white text-xs sm:text-sm"
                >
                  <DollarSign size={12} className="mr-1 sm:mr-2" />
                  Sales
                </TabsTrigger>
                <TabsTrigger 
                  value="operation" 
                  data-testid="dept-tab-operation"
                  className="data-[state=active]:bg-[#3B82F6] data-[state=active]:text-white text-xs sm:text-sm"
                >
                  <Target size={12} className="mr-1 sm:mr-2" />
                  Operation
                </TabsTrigger>
              </TabsList>

              {/* Month Navigation & Broker Config */}
              <div className="flex items-center gap-2 sm:gap-3">
                {/* Month Navigation */}
                <div className="flex items-center gap-1 bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded-lg px-1 py-1">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-7 w-7 p-0 text-white hover:bg-white/10"
                    onClick={goToPreviousMonth}
                  >
                    <ChevronLeft size={14} />
                  </Button>
                  <span className="text-xs font-semibold text-white min-w-[100px] text-center">
                    {shortMonthNames[currentMonth]} {currentYear}
                  </span>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-7 w-7 p-0 text-white hover:bg-white/10"
                    onClick={goToNextMonth}
                  >
                    <ChevronRight size={14} />
                  </Button>
                </div>
                
                {/* Broker Config Button (Sales Tab Only) */}
                {activeTab === 'sales' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowBrokerConfig(true)}
                    className="border-[hsl(var(--border))] text-white h-8"
                    data-testid="configure-brokers-btn"
                  >
                    <Settings size={14} className="mr-2" />
                    Configure Brokers
                  </Button>
                )}
              </div>
            </div>

            {/* Marketing Tab */}
            <TabsContent value="marketing" className="mt-0">
              <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
                <SectionHeader 
                  icon={TrendingUp} 
                  title="Marketing Daily Data" 
                  subtitle={`${monthNames[currentMonth]} ${currentYear} • ${filterByMonth(marketingData).length} entries`}
                  color="#E63946"
                />
                
                <div className="overflow-x-auto">
                  {/* Bulk Mode Toggle + Action Bar */}
                  <div className="flex items-center justify-between px-4 py-2 border-b border-[hsl(var(--border))] bg-[hsl(var(--surface-elevated))]/40">
                    {bulkMode ? (
                      <>
                        <span className="text-xs text-white" data-testid="bulk-mkt-count">
                          {selectedMkt.size > 0 ? <><b>{selectedMkt.size}</b> selected</> : 'Tick rows to select'}
                        </span>
                        <div className="flex gap-2">
                          {selectedMkt.size > 0 && (
                            <Button size="sm" className="h-7 bg-[#E63946] hover:bg-[#E63946]/90 text-xs" onClick={() => handleBulkDelete('marketing')} disabled={isBulkDeleting} data-testid="bulk-mkt-delete">
                              <Trash2 size={12} className="mr-1" /> Delete Selected ({selectedMkt.size})
                            </Button>
                          )}
                          <Button size="sm" variant="outline" className="h-7 text-xs text-white border-[hsl(var(--border))]" onClick={() => { setBulkMode(false); setSelectedMkt(new Set()); }} data-testid="bulk-mkt-exit">
                            Cancel
                          </Button>
                        </div>
                      </>
                    ) : (
                      <Button size="sm" variant="outline" className="h-7 text-xs text-white border-[hsl(var(--border))]" onClick={() => setBulkMode(true)} data-testid="bulk-mkt-enter">
                        <CheckSquare size={12} className="mr-1" /> Select Multiple
                      </Button>
                    )}
                  </div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[hsl(var(--border))] bg-[hsl(var(--surface-elevated))]">
                        {bulkMode && (
                          <th className="py-3 px-2 w-8">
                            <input
                              type="checkbox"
                              className="cursor-pointer"
                              checked={selectedMkt.size === filterByMonth(marketingData).length && filterByMonth(marketingData).length > 0}
                              onChange={() => toggleSelectAll(setSelectedMkt, selectedMkt, filterByMonth(marketingData))}
                              data-testid="bulk-mkt-select-all"
                              aria-label="Select all marketing"
                            />
                          </th>
                        )}
                        <th className="text-left py-3 px-4 text-[#6B7280] font-semibold text-xs">Date</th>
                        <th className="text-right py-3 px-4 text-[#6B7280] font-semibold text-xs">
                          <span className="border-b border-dashed border-[#F59E0B] cursor-help">Total Spend</span>
                        </th>
                        <th className="text-right py-3 px-4 text-[#6B7280] font-semibold text-xs">
                          <span className="border-b border-dashed border-[#F59E0B] cursor-help">Total Impressions</span>
                        </th>
                        <th className="text-right py-3 px-4 text-[#6B7280] font-semibold text-xs">
                          <span className="border-b border-dashed border-[#F59E0B] cursor-help">Total Clicks</span>
                        </th>
                        <th className="text-right py-3 px-4 text-[#6B7280] font-semibold text-xs">LPV</th>
                        <th className="text-right py-3 px-4 text-[#6B7280] font-semibold text-xs">CTA</th>
                        <th className="text-right py-3 px-4 text-[#6B7280] font-semibold text-xs">Subs FT</th>
                        <th className="text-right py-3 px-4 text-[#6B7280] font-semibold text-xs">Subs TG</th>
                        <th className="text-right py-3 px-4 text-[#6B7280] font-semibold text-xs">Subs Bot</th>
                        <th className="text-right py-3 px-4 text-[#F97316] font-semibold text-xs">CPS</th>
                        <th className="text-right py-3 px-4 text-[#EC4899] font-semibold text-xs">CPS (FT)</th>
                        <th className="text-center py-3 px-4 text-[#6B7280] font-semibold text-xs">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filterByMonth(marketingData).length === 0 ? (
                        <tr>
                          <td colSpan={10} className="text-center py-12 text-[#6B7280]">
                            <TrendingUp size={32} className="mx-auto mb-2 opacity-50" />
                            <p>No data for {monthNames[currentMonth]} {currentYear}</p>
                            <Button
                              variant="outline"
                              size="sm"
                              className="mt-3 border-[hsl(var(--border))] text-white"
                              onClick={() => setShowCreateModal(true)}
                            >
                              <Plus size={14} className="mr-1" />
                              Add Entry
                            </Button>
                          </td>
                        </tr>
                      ) : (
                        filterByMonth(marketingData).sort((a, b) => a.date.localeCompare(b.date)).map((row) => (
                          <tr key={row.id} className={`border-b border-[hsl(var(--border))] hover:bg-white/5 transition-colors ${bulkMode && selectedMkt.has(row) ? 'bg-[#E63946]/5' : ''}`}>
                            {bulkMode && (
                              <td className="py-3 px-2 text-center">
                                <input
                                  type="checkbox"
                                  className="cursor-pointer"
                                  checked={selectedMkt.has(row)}
                                  onChange={() => toggleSelect(setSelectedMkt, selectedMkt, row)}
                                  data-testid={`bulk-mkt-row-${row.date}`}
                                  aria-label={`Select marketing ${row.date}`}
                                />
                              </td>
                            )}
                            <td className="py-3 px-4 text-white font-medium">
                              {new Date(row.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                            </td>
                            
                            {/* Total Spend with Tooltip */}
                            <td className="py-3 px-4 text-right">
                              <div 
                                className="inline-block cursor-pointer"
                                onMouseEnter={(e) => {
                                  setHoveredCell(`spend-${row.id}`);
                                  setTooltipPos({ x: e.clientX - 140, y: e.clientY });
                                }}
                                onMouseLeave={() => setHoveredCell(null)}
                              >
                                <span className="text-[#10B981] font-semibold border-b border-dashed border-[#10B981]/50">
                                  RM {calcTotal(row.spend).toLocaleString()}
                                </span>
                              </div>
                              {hoveredCell === `spend-${row.id}` && (
                                <PlatformTooltip data={row.spend} type="currency" label="Spend" position={tooltipPos} hoveredCell={hoveredCell} />
                              )}
                            </td>
                            
                            {/* Total Impressions with Tooltip */}
                            <td className="py-3 px-4 text-right">
                              <div 
                                className="inline-block cursor-pointer"
                                onMouseEnter={(e) => {
                                  setHoveredCell(`impr-${row.id}`);
                                  setTooltipPos({ x: e.clientX - 140, y: e.clientY });
                                }}
                                onMouseLeave={() => setHoveredCell(null)}
                              >
                                <span className="text-white border-b border-dashed border-white/30">
                                  {calcTotal(row.impressions).toLocaleString()}
                                </span>
                              </div>
                              {hoveredCell === `impr-${row.id}` && (
                                <PlatformTooltip data={row.impressions} type="number" label="Impressions" position={tooltipPos} hoveredCell={hoveredCell} />
                              )}
                            </td>
                            
                            {/* Total Clicks with Tooltip */}
                            <td className="py-3 px-4 text-right">
                              <div 
                                className="inline-block cursor-pointer"
                                onMouseEnter={(e) => {
                                  setHoveredCell(`clicks-${row.id}`);
                                  setTooltipPos({ x: e.clientX - 140, y: e.clientY });
                                }}
                                onMouseLeave={() => setHoveredCell(null)}
                              >
                                <span className="text-white border-b border-dashed border-white/30">
                                  {calcTotal(row.clicks).toLocaleString()}
                                </span>
                              </div>
                              {hoveredCell === `clicks-${row.id}` && (
                                <PlatformTooltip data={row.clicks} type="number" label="Clicks" position={tooltipPos} hoveredCell={hoveredCell} />
                              )}
                            </td>
                            
                            <td className="py-3 px-4 text-right text-white">{row.lpv?.toLocaleString()}</td>
                            <td className="py-3 px-4 text-right text-white">{row.cta?.toLocaleString()}</td>
                            <td className="py-3 px-4 text-right text-[#EC4899]">{(row.subsFreeTrial || 0).toLocaleString()}</td>
                            <td className="py-3 px-4 text-right text-[#0088CC]">{row.subsTG?.toLocaleString()}</td>
                            <td className="py-3 px-4 text-right text-[#8B5CF6]">{row.subsBot?.toLocaleString()}</td>
                            <td className="py-3 px-4 text-right text-[#F97316] font-semibold">
                              {(() => {
                                const totalSubs = (row.subsTG || 0) + (row.subsBot || 0);
                                const totalSpend = calcTotal(row.spend);
                                return totalSubs > 0 ? `RM ${(totalSpend / totalSubs).toFixed(2)}` : '-';
                              })()}
                            </td>
                            <td className="py-3 px-4 text-right text-[#EC4899] font-semibold">
                              {(() => {
                                const ft = row.subsFreeTrial || 0;
                                const totalSpend = calcTotal(row.spend);
                                return ft > 0 ? `RM ${(totalSpend / ft).toFixed(2)}` : '-';
                              })()}
                            </td>
                            
                            <td className="py-3 px-4 text-center">
                              <div className="flex items-center justify-center gap-1">
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="h-7 w-7 p-0 text-[#10B981] hover:bg-[#10B981]/20"
                                  onClick={() => handleShareRow(row, 'marketing')}
                                  title="Share / Copy"
                                >
                                  <Share2 size={14} />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="h-7 w-7 p-0 text-[#3B82F6] hover:bg-[#3B82F6]/20"
                                  onClick={() => { setEditingEntry({ ...row, _sourceRef: row }); setShowEditModal(true); }}
                                >
                                  <Edit2 size={14} />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="h-7 w-7 p-0 text-[#E63946] hover:bg-[#E63946]/20"
                                  onClick={() => confirmDelete(row.id, row.date, row)}
                                >
                                  <Trash2 size={14} />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                      {/* TOTAL row — sum across month */}
                      {filterByMonth(marketingData).length > 0 && (() => {
                        const rows = filterByMonth(marketingData);
                        const sumSpend = rows.reduce((s, r) => s + calcTotal(r.spend), 0);
                        const sumImp = rows.reduce((s, r) => s + calcTotal(r.impressions), 0);
                        const sumClicks = rows.reduce((s, r) => s + calcTotal(r.clicks), 0);
                        const sumLPV = rows.reduce((s, r) => s + (Number(r.lpv) || 0), 0);
                        const sumCTA = rows.reduce((s, r) => s + (Number(r.cta) || 0), 0);
                        const sumFT = rows.reduce((s, r) => s + (Number(r.subsFreeTrial) || 0), 0);
                        const sumTG = rows.reduce((s, r) => s + (Number(r.subsTG) || 0), 0);
                        const sumBot = rows.reduce((s, r) => s + (Number(r.subsBot) || 0), 0);
                        const totalSubs = sumTG + sumBot;
                        const monthCPS = totalSubs > 0 ? sumSpend / totalSubs : 0;
                        const monthCPSFT = sumFT > 0 ? sumSpend / sumFT : 0;
                        return (
                          <tr className="border-t-2 border-[#F97316]/40 bg-[#F97316]/8 font-bold" data-testid="marketing-total-row">
                            {bulkMode && <td className="py-3 px-2"></td>}
                            <td className="py-3 px-4 text-[#F97316] font-bold uppercase tracking-wider text-xs">TOTAL</td>
                            <td className="py-3 px-4 text-right text-[#10B981] font-bold">RM {sumSpend.toLocaleString(undefined,{maximumFractionDigits:2})}</td>
                            <td className="py-3 px-4 text-right text-white">{sumImp.toLocaleString()}</td>
                            <td className="py-3 px-4 text-right text-white">{sumClicks.toLocaleString()}</td>
                            <td className="py-3 px-4 text-right text-white">{sumLPV.toLocaleString()}</td>
                            <td className="py-3 px-4 text-right text-white">{sumCTA.toLocaleString()}</td>
                            <td className="py-3 px-4 text-right text-[#EC4899]">{sumFT.toLocaleString()}</td>
                            <td className="py-3 px-4 text-right text-[#3B82F6]">{sumTG.toLocaleString()}</td>
                            <td className="py-3 px-4 text-right text-[#8B5CF6]">{sumBot.toLocaleString()}</td>
                            <td className="py-3 px-4 text-right text-[#F97316]">{monthCPS > 0 ? `RM ${monthCPS.toFixed(2)}` : '—'}</td>
                            <td className="py-3 px-4 text-right text-[#EC4899]">{monthCPSFT > 0 ? `RM ${monthCPSFT.toFixed(2)}` : '—'}</td>
                            <td className="py-3 px-4"></td>
                          </tr>
                        );
                      })()}
                    </tbody>
                  </table>
                </div>
                
                {/* Legend */}
                <div className="px-4 py-3 border-t border-[hsl(var(--border))] bg-[hsl(var(--surface-elevated))]">
                  <p className="text-[10px] text-[#6B7280] flex items-center gap-4">
                    <span className="flex items-center gap-1">
                      <span className="w-3 border-b border-dashed border-[#F59E0B]"></span>
                      Hover for platform breakdown
                    </span>
                  </p>
                </div>
              </div>
            </TabsContent>

            {/* Sales Tab */}
            <TabsContent value="sales" className="mt-0">
              <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
                <SectionHeader 
                  icon={DollarSign} 
                  title="Sales Daily Data" 
                  subtitle={`${monthNames[currentMonth]} ${currentYear} • ${filterByMonth(salesData).length} entries`}
                  color="#10B981"
                />
                
                <div className="overflow-x-auto">
                  <div className="flex items-center justify-between px-4 py-2 border-b border-[hsl(var(--border))] bg-[hsl(var(--surface-elevated))]/40">
                    {bulkMode ? (
                      <>
                        <span className="text-xs text-white" data-testid="bulk-sales-count">
                          {selectedSales.size > 0 ? <><b>{selectedSales.size}</b> selected</> : 'Tick rows to select'}
                        </span>
                        <div className="flex gap-2">
                          {selectedSales.size > 0 && (
                            <Button size="sm" className="h-7 bg-[#E63946] hover:bg-[#E63946]/90 text-xs" onClick={() => handleBulkDelete('sales')} disabled={isBulkDeleting} data-testid="bulk-sales-delete">
                              <Trash2 size={12} className="mr-1" /> Delete Selected ({selectedSales.size})
                            </Button>
                          )}
                          <Button size="sm" variant="outline" className="h-7 text-xs text-white border-[hsl(var(--border))]" onClick={() => { setBulkMode(false); setSelectedSales(new Set()); }} data-testid="bulk-sales-exit">
                            Cancel
                          </Button>
                        </div>
                      </>
                    ) : (
                      <Button size="sm" variant="outline" className="h-7 text-xs text-white border-[hsl(var(--border))]" onClick={() => setBulkMode(true)} data-testid="bulk-sales-enter">
                        <CheckSquare size={12} className="mr-1" /> Select Multiple
                      </Button>
                    )}
                  </div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[hsl(var(--border))] bg-[hsl(var(--surface-elevated))]">
                        {bulkMode && (
                          <th className="py-3 px-2 w-8 sticky left-0 z-20 bg-[hsl(var(--surface-elevated))]">
                            <input
                              type="checkbox"
                              className="cursor-pointer"
                              checked={selectedSales.size === filterByMonth(salesData).length && filterByMonth(salesData).length > 0}
                              onChange={() => toggleSelectAll(setSelectedSales, selectedSales, filterByMonth(salesData))}
                              data-testid="bulk-sales-select-all"
                              aria-label="Select all sales"
                            />
                          </th>
                        )}
                        <th className={`text-left py-3 px-4 text-[#6B7280] font-semibold text-xs min-w-[80px] sticky ${bulkMode ? 'left-8' : 'left-0'} z-20 bg-[hsl(var(--surface-elevated))]`}>Date</th>
                        
                        {/* Leads - Sales Only */}
                        <th colSpan={3} className="text-center py-2 px-3 text-xs border-l border-[hsl(var(--border))]">
                          <span className="text-[#10B981] font-semibold">Leads</span>
                          <div className="flex text-[10px] text-[#6B7280] mt-1 gap-2">
                            <span className="flex-1 text-center">New</span>
                            <span className="flex-1 text-center">Pros</span>
                            <span className="flex-1 text-center">VIP</span>
                          </div>
                        </th>
                        
                        <th className="text-center py-3 px-4 text-[#F59E0B] font-semibold text-xs border-l border-[hsl(var(--border))]">Total Leads</th>
                        <th className="text-center py-3 px-4 text-[#3B82F6] font-semibold text-xs">New Closed</th>
                        <th className="text-center py-3 px-4 text-[#8B5CF6] font-semibold text-xs">Returning Closed</th>
                        <th className="text-center py-3 px-4 text-[#10B981] font-semibold text-xs">Total Closed</th>
                        <th className="text-center py-3 px-4 text-[#F97316] font-semibold text-xs">Closing Rate</th>
                        
                        {/* Dynamic Broker Columns */}
                        {enabledBrokers.map(([key, config]) => (
                          <th key={key} colSpan={5} className="text-center py-2 px-3 text-xs border-l border-[hsl(var(--border))]">
                            <span className="text-white font-semibold">{config.name}</span>
                            <div className="flex text-[10px] text-[#6B7280] mt-1 gap-1">
                              <span className="flex-1 text-center">Act</span>
                              <span className="flex-1 text-center">Lots</span>
                              <span className="flex-1 text-center">Comm ($)</span>
                              <span className="flex-1 text-center">Com/Lot</span>
                              <span className="flex-1 text-center">Depo ($)</span>
                            </div>
                          </th>
                        ))}
                        
                        {/* TOTAL Columns (auto-sum from all brokers) */}
                        <th colSpan={5} className="text-center py-2 px-3 text-xs border-l-2 border-[#10B981] bg-[#10B981]/10">
                          <span className="text-[#10B981] font-bold">TOTAL</span>
                          <div className="flex text-[10px] text-[#6B7280] mt-1 gap-1">
                            <span className="flex-1 text-center">Act</span>
                            <span className="flex-1 text-center">Lots</span>
                            <span className="flex-1 text-center">Comm ($)</span>
                            <span className="flex-1 text-center">Com/Lot</span>
                            <span className="flex-1 text-center">Depo ($)</span>
                          </div>
                        </th>
                        
                        {/* Funnel columns */}
                        <th className="text-center py-3 px-3 text-[#6B7280] font-semibold text-xs border-l border-[hsl(var(--border))]">Greet</th>
                        <th className="text-center py-3 px-3 text-[#6B7280] font-semibold text-xs">1Tick</th>
                        <th className="text-center py-3 px-3 text-[#6B7280] font-semibold text-xs">Reg</th>
                        <th className="text-center py-3 px-3 text-[#6B7280] font-semibold text-xs">Broker</th>
                        <th className="text-center py-3 px-3 text-[#6B7280] font-semibold text-xs">Price</th>
                        <th className="text-center py-3 px-3 text-[#6B7280] font-semibold text-xs">Others</th>
                        
                        <th className="text-center py-3 px-4 text-[#6B7280] font-semibold text-xs border-l border-[hsl(var(--border))]">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filterByMonth(salesData).length === 0 ? (
                        <tr>
                          <td colSpan={27} className="text-center py-12 text-[#6B7280]">
                            <DollarSign size={32} className="mx-auto mb-2 opacity-50" />
                            <p>No data for {monthNames[currentMonth]} {currentYear}</p>
                          </td>
                        </tr>
                      ) : (
                        filterByMonth(salesData).sort((a, b) => a.date.localeCompare(b.date)).map((row) => {
                          // Calculate total leads from all sources
                          const totalLeads = (row.leadsM?.new || 0) + (row.leadsM?.prospect || 0) + (row.leadsM?.vip || 0) +
                                            (row.leadsS?.new || 0) + (row.leadsS?.prospect || 0) + (row.leadsS?.vip || 0) +
                                            (row.leadsIB?.new || 0) + (row.leadsIB?.prospect || 0) + (row.leadsIB?.vip || 0) || row.leads || 0;
                          return (
                          <tr key={row.id} className={`border-b border-[hsl(var(--border))] hover:bg-white/5 ${bulkMode && selectedSales.has(row) ? 'bg-[#10B981]/5' : ''}`}>
                            {bulkMode && (
                              <td className="py-3 px-2 text-center sticky left-0 z-10 bg-[hsl(var(--surface))]">
                                <input
                                  type="checkbox"
                                  className="cursor-pointer"
                                  checked={selectedSales.has(row)}
                                  onChange={() => toggleSelect(setSelectedSales, selectedSales, row)}
                                  data-testid={`bulk-sales-row-${row.date}`}
                                  aria-label={`Select sales ${row.date}`}
                                />
                              </td>
                            )}
                            <td className={`py-3 px-4 text-white font-medium sticky ${bulkMode ? 'left-8' : 'left-0'} z-10 bg-[hsl(var(--surface))]`}>
                              {new Date(row.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                            </td>
                            
                            {/* Leads - Combined (M+S+IB → Sales) */}
                            <td className="py-3 px-3 text-center text-white text-sm border-l border-[hsl(var(--border))]">{(row.leadsS?.new || 0) + (row.leadsM?.new || 0) + (row.leadsIB?.new || 0)}</td>
                            <td className="py-3 px-3 text-center text-white text-sm">{(row.leadsS?.prospect || 0) + (row.leadsM?.prospect || 0) + (row.leadsIB?.prospect || 0)}</td>
                            <td className="py-3 px-3 text-center text-white text-sm">{(row.leadsS?.vip || 0) + (row.leadsM?.vip || 0) + (row.leadsIB?.vip || 0)}</td>
                            
                            <td className="py-3 px-4 text-center text-[#F59E0B] font-semibold border-l border-[hsl(var(--border))]">{totalLeads}</td>
                            <td className="py-3 px-4 text-center text-[#3B82F6] font-semibold">{row.newClosed || row.closed || 0}</td>
                            <td className="py-3 px-4 text-center text-[#8B5CF6] font-semibold">{row.vipClosed || 0}</td>
                            <td className="py-3 px-4 text-center text-[#10B981] font-semibold">{(row.newClosed || row.closed || 0) + (row.vipClosed || 0)}</td>
                            <td className="py-3 px-4 text-center text-[#F97316] font-semibold">
                              {totalLeads > 0 
                                ? `${(((row.newClosed || row.closed || 0) + (row.vipClosed || 0)) / totalLeads * 100).toFixed(1)}%`
                                : '0.0%'}
                            </td>
                            
                            {/* Broker Data */}
                            {enabledBrokers.map(([key]) => {
                              const brokerData = row[key] || { active: 0, lots: 0, commission: 0, deposit: 0 };
                              const comLot = brokerData.lots > 0 ? (brokerData.commission / brokerData.lots) : 0;
                              return (
                                <React.Fragment key={key}>
                                  <td className="py-3 px-3 text-center text-white text-sm border-l border-[hsl(var(--border))]">{brokerData.active}</td>
                                  <td className="py-3 px-3 text-center text-white text-sm">{Number(brokerData.lots || 0).toFixed(2)}</td>
                                  <td className="py-3 px-3 text-center text-[#10B981] text-sm">{Number(brokerData.commission || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                                  <td className={`py-3 px-3 text-center text-sm font-semibold ${comLot >= 12 ? 'text-[#10B981]' : comLot >= 8 ? 'text-[#F59E0B]' : comLot > 0 ? 'text-[#E63946]' : 'text-[#6B7280]'}`}>{comLot > 0 ? `$${comLot.toFixed(2)}` : '-'}</td>
                                  <td className="py-3 px-3 text-center text-[#3B82F6] text-sm">{Number(brokerData.deposit || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                                </React.Fragment>
                              );
                            })}
                            
                            {/* TOTAL Columns (auto-sum from all brokers) */}
                            {(() => {
                              const totalActive = enabledBrokers.reduce((sum, [key]) => sum + (row[key]?.active || 0), 0);
                              const totalLots = Math.round(enabledBrokers.reduce((sum, [key]) => sum + (row[key]?.lots || 0), 0) * 100) / 100;
                              const totalComm = Math.round(enabledBrokers.reduce((sum, [key]) => sum + (row[key]?.commission || 0), 0) * 100) / 100;
                              const totalDepo = Math.round(enabledBrokers.reduce((sum, [key]) => sum + (row[key]?.deposit || 0), 0) * 100) / 100;
                              const totalComLot = totalLots > 0 ? (totalComm / totalLots) : 0;
                              return (
                                <>
                                  <td className="py-3 px-3 text-center text-[#10B981] font-semibold text-sm border-l-2 border-[#10B981] bg-[#10B981]/5">{totalActive}</td>
                                  <td className="py-3 px-3 text-center text-[#10B981] font-semibold text-sm bg-[#10B981]/5">{totalLots.toFixed(2)}</td>
                                  <td className="py-3 px-3 text-center text-[#10B981] font-bold text-sm bg-[#10B981]/5">${totalComm.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                                  <td className={`py-3 px-3 text-center font-bold text-sm bg-[#10B981]/5 ${totalComLot >= 12 ? 'text-[#10B981]' : totalComLot >= 8 ? 'text-[#F59E0B]' : totalComLot > 0 ? 'text-[#E63946]' : 'text-[#6B7280]'}`}>{totalComLot > 0 ? `$${totalComLot.toFixed(2)}` : '-'}</td>
                                  <td className="py-3 px-3 text-center text-[#10B981] font-bold text-sm bg-[#10B981]/5">${totalDepo.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                                </>
                              );
                            })()}
                            
                            {/* Funnel */}
                            <td className="py-3 px-3 text-center text-[#9CA3AF] text-sm border-l border-[hsl(var(--border))]">{row.greeting}</td>
                            <td className="py-3 px-3 text-center text-[#9CA3AF] text-sm">{row.oneTick}</td>
                            <td className="py-3 px-3 text-center text-[#9CA3AF] text-sm">{row.registration}</td>
                            <td className="py-3 px-3 text-center text-[#9CA3AF] text-sm">{row.broker}</td>
                            <td className="py-3 px-3 text-center text-[#9CA3AF] text-sm">{row.priceOffer}</td>
                            <td className="py-3 px-3 text-center text-[#9CA3AF] text-sm">{row.others}</td>
                            
                            <td className="py-3 px-4 text-center border-l border-[hsl(var(--border))]">
                              <div className="flex items-center justify-center gap-1">
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="h-7 w-7 p-0 text-[#10B981] hover:bg-[#10B981]/20"
                                  onClick={() => handleShareRow(row, 'sales')}
                                  title="Share / Copy"
                                >
                                  <Share2 size={14} />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="h-7 w-7 p-0 text-[#3B82F6] hover:bg-[#3B82F6]/20"
                                  onClick={() => { setEditingEntry({ ...row, _sourceRef: row }); setShowEditModal(true); }}
                                >
                                  <Edit2 size={14} />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="h-7 w-7 p-0 text-[#E63946] hover:bg-[#E63946]/20"
                                  onClick={() => confirmDelete(row.id, row.date, row)}
                                >
                                  <Trash2 size={14} />
                                </Button>
                              </div>
                            </td>
                          </tr>
                          );
                        })
                      )}
                      {/* TOTAL row — month sum for Sales */}
                      {filterByMonth(salesData).length > 0 && (() => {
                        const rows = filterByMonth(salesData);
                        const sumNew = rows.reduce((s,r) => s + ((r.leadsS?.new||0)+(r.leadsM?.new||0)+(r.leadsIB?.new||0)), 0);
                        const sumPros = rows.reduce((s,r) => s + ((r.leadsS?.prospect||0)+(r.leadsM?.prospect||0)+(r.leadsIB?.prospect||0)), 0);
                        const sumVIP = rows.reduce((s,r) => s + ((r.leadsS?.vip||0)+(r.leadsM?.vip||0)+(r.leadsIB?.vip||0)), 0);
                        const totalLeadsSum = rows.reduce((s,r) => {
                          const tl = (r.leadsM?.new||0)+(r.leadsM?.prospect||0)+(r.leadsM?.vip||0)
                                   + (r.leadsS?.new||0)+(r.leadsS?.prospect||0)+(r.leadsS?.vip||0)
                                   + (r.leadsIB?.new||0)+(r.leadsIB?.prospect||0)+(r.leadsIB?.vip||0) || r.leads || 0;
                          return s + tl;
                        }, 0);
                        const sumNewClosed = rows.reduce((s,r) => s + (r.newClosed||r.closed||0), 0);
                        const sumVipClosed = rows.reduce((s,r) => s + (r.vipClosed||0), 0);
                        const sumTotalClosed = sumNewClosed + sumVipClosed;
                        const monthCR = totalLeadsSum > 0 ? (sumTotalClosed / totalLeadsSum * 100) : 0;
                        const sumGreeting = rows.reduce((s,r) => s + (Number(r.greeting)||0), 0);
                        const sumOneTick = rows.reduce((s,r) => s + (Number(r.oneTick)||0), 0);
                        const sumReg = rows.reduce((s,r) => s + (Number(r.registration)||0), 0);
                        const sumBroker = rows.reduce((s,r) => s + (Number(r.broker)||0), 0);
                        const sumPrice = rows.reduce((s,r) => s + (Number(r.priceOffer)||0), 0);
                        const sumOthers = rows.reduce((s,r) => s + (Number(r.others)||0), 0);

                        // Broker totals per enabled broker
                        const brokerTotals = enabledBrokers.map(([key]) => {
                          const a = rows.reduce((s,r) => s + (r[key]?.active||0), 0);
                          const l = Math.round(rows.reduce((s,r) => s + (r[key]?.lots||0), 0)*100)/100;
                          const c = Math.round(rows.reduce((s,r) => s + (r[key]?.commission||0), 0)*100)/100;
                          const d = Math.round(rows.reduce((s,r) => s + (r[key]?.deposit||0), 0)*100)/100;
                          const cl = l > 0 ? c/l : 0;
                          return { a, l, c, d, cl };
                        });
                        const grandActive = brokerTotals.reduce((s,b) => s + b.a, 0);
                        const grandLots = Math.round(brokerTotals.reduce((s,b) => s + b.l, 0)*100)/100;
                        const grandComm = Math.round(brokerTotals.reduce((s,b) => s + b.c, 0)*100)/100;
                        const grandDepo = Math.round(brokerTotals.reduce((s,b) => s + b.d, 0)*100)/100;
                        const grandComLot = grandLots > 0 ? grandComm / grandLots : 0;

                        return (
                          <tr className="border-t-2 border-[#10B981]/40 bg-[#10B981]/8 font-bold" data-testid="sales-total-row">
                            {bulkMode && <td className="py-3 px-2 sticky left-0 z-10 bg-[hsl(var(--surface))]"></td>}
                            <td className={`py-3 px-4 text-[#10B981] font-bold uppercase tracking-wider text-xs sticky ${bulkMode ? 'left-8' : 'left-0'} z-10 bg-[hsl(var(--surface))]`}>TOTAL</td>
                            <td className="py-3 px-3 text-center text-white border-l border-[hsl(var(--border))]">{sumNew}</td>
                            <td className="py-3 px-3 text-center text-white">{sumPros}</td>
                            <td className="py-3 px-3 text-center text-white">{sumVIP}</td>
                            <td className="py-3 px-4 text-center text-[#F59E0B] border-l border-[hsl(var(--border))]">{totalLeadsSum}</td>
                            <td className="py-3 px-4 text-center text-[#3B82F6]">{sumNewClosed}</td>
                            <td className="py-3 px-4 text-center text-[#8B5CF6]">{sumVipClosed}</td>
                            <td className="py-3 px-4 text-center text-[#10B981]">{sumTotalClosed}</td>
                            <td className="py-3 px-4 text-center text-[#F97316]">{monthCR.toFixed(1)}%</td>
                            {brokerTotals.map((b, i) => (
                              <React.Fragment key={i}>
                                <td className="py-3 px-3 text-center text-white text-sm border-l border-[hsl(var(--border))]">{b.a}</td>
                                <td className="py-3 px-3 text-center text-white text-sm">{b.l}</td>
                                <td className="py-3 px-3 text-center text-[#10B981] text-sm">{b.c.toLocaleString()}</td>
                                <td className={`py-3 px-3 text-center text-sm ${b.cl >= 12 ? 'text-[#10B981]' : b.cl >= 8 ? 'text-[#F59E0B]' : b.cl > 0 ? 'text-[#E63946]' : 'text-[#6B7280]'}`}>{b.cl > 0 ? `$${b.cl.toFixed(2)}` : '-'}</td>
                                <td className="py-3 px-3 text-center text-[#3B82F6] text-sm">{b.d.toLocaleString()}</td>
                              </React.Fragment>
                            ))}
                            <td className="py-3 px-3 text-center text-[#10B981] text-sm border-l-2 border-[#10B981] bg-[#10B981]/10">{grandActive}</td>
                            <td className="py-3 px-3 text-center text-[#10B981] text-sm bg-[#10B981]/10">{grandLots}</td>
                            <td className="py-3 px-3 text-center text-[#10B981] text-sm bg-[#10B981]/10">${grandComm.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
                            <td className={`py-3 px-3 text-center text-sm bg-[#10B981]/10 ${grandComLot >= 12 ? 'text-[#10B981]' : grandComLot >= 8 ? 'text-[#F59E0B]' : grandComLot > 0 ? 'text-[#E63946]' : 'text-[#6B7280]'}`}>{grandComLot > 0 ? `$${grandComLot.toFixed(2)}` : '-'}</td>
                            <td className="py-3 px-3 text-center text-[#10B981] text-sm bg-[#10B981]/10">${grandDepo.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
                            <td className="py-3 px-3 text-center text-[#9CA3AF] text-sm border-l border-[hsl(var(--border))]">{sumGreeting}</td>
                            <td className="py-3 px-3 text-center text-[#9CA3AF] text-sm">{sumOneTick}</td>
                            <td className="py-3 px-3 text-center text-[#9CA3AF] text-sm">{sumReg}</td>
                            <td className="py-3 px-3 text-center text-[#9CA3AF] text-sm">{sumBroker}</td>
                            <td className="py-3 px-3 text-center text-[#9CA3AF] text-sm">{sumPrice}</td>
                            <td className="py-3 px-3 text-center text-[#9CA3AF] text-sm">{sumOthers}</td>
                            <td className="py-3 px-4 border-l border-[hsl(var(--border))]"></td>
                          </tr>
                        );
                      })()}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>

            {/* Operation Tab */}
            <TabsContent value="operation" className="mt-0">
              <TelegramSyncStatusCard ibId={ib?.id} />
              <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
                <SectionHeader 
                  icon={Target} 
                  title="Operation Daily Data" 
                  subtitle={`${monthNames[currentMonth]} ${currentYear} • Trade Signals & Performance`}
                  color="#3B82F6"
                />
                
                <div className="p-4 space-y-4">
                  {/* Operation bulk-action toolbar — only when bulkMode is active */}
                  {(() => {
                    const opsInMonth = operationData.filter(d => {
                      const date = new Date(d.date);
                      return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
                    });
                    if (opsInMonth.length === 0) return null;
                    return (
                      <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded-md">
                        {bulkMode ? (
                          <>
                            <div className="flex items-center gap-3">
                              <input
                                type="checkbox"
                                className="cursor-pointer"
                                checked={selectedOps.size === opsInMonth.length}
                                onChange={() => toggleSelectAll(setSelectedOps, selectedOps, opsInMonth.map(d => d.date))}
                                data-testid="bulk-ops-select-all"
                                aria-label="Select all operation days"
                              />
                              <span className="text-xs text-white">
                                {selectedOps.size > 0 ? <><b>{selectedOps.size}</b> day{selectedOps.size > 1 ? 's' : ''} selected</> : 'Tick days to select'}
                              </span>
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                              {selectedOps.size > 0 && (
                                <div className="flex flex-wrap items-center gap-2" data-testid="bulk-ops-provider">
                                  <ProviderSelect value={bulkProvider} onChange={setBulkProvider} className="h-8 w-40 text-xs" placeholder="Pilih provider" testid="bulk-ops-provider-select" />
                                  <label className="flex items-center gap-1.5 text-xs text-[#9CA3AF] cursor-pointer">
                                    <input type="checkbox" checked={bulkOnlyUnset} onChange={(e) => setBulkOnlyUnset(e.target.checked)} data-testid="bulk-ops-provider-only-unset" />
                                    Hanya yang belum ditetapkan
                                  </label>
                                  <Button size="sm" className="h-8 text-xs bg-[#EC4899] hover:bg-[#EC4899]/90 text-white" disabled={!bulkProvider || settingProvider} onClick={handleBulkSetProvider} data-testid="bulk-ops-set-provider">
                                    Set Provider ({selectedOps.size} hari)
                                  </Button>
                                </div>
                              )}
                              {selectedOps.size > 0 && (
                                <Button size="sm" className="h-7 bg-[#E63946] hover:bg-[#E63946]/90 text-xs" onClick={() => handleBulkDelete('operation')} disabled={isBulkDeleting} data-testid="bulk-ops-delete">
                                  <Trash2 size={12} className="mr-1" /> Delete Selected Days ({selectedOps.size})
                                </Button>
                              )}
                              <Button size="sm" variant="outline" className="h-7 text-xs text-white border-[hsl(var(--border))]" onClick={() => { setBulkMode(false); setSelectedOps(new Set()); }} data-testid="bulk-ops-exit">
                                Cancel
                              </Button>
                            </div>
                          </>
                        ) : (
                          <Button size="sm" variant="outline" className="h-7 text-xs text-white border-[hsl(var(--border))]" onClick={() => setBulkMode(true)} data-testid="bulk-ops-enter">
                            <CheckSquare size={12} className="mr-1" /> Select Multiple Days
                          </Button>
                        )}
                      </div>
                    );
                  })()}
                  {operationData.filter(d => {
                    const date = new Date(d.date);
                    return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
                  }).length === 0 ? (
                    <div className="text-center py-12 text-[#6B7280]">
                      <Target size={32} className="mx-auto mb-2 opacity-50" />
                      <p>No trades for {monthNames[currentMonth]} {currentYear}</p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-3 border-[hsl(var(--border))] text-white"
                        onClick={() => setShowCreateModal(true)}
                      >
                        <Plus size={14} className="mr-1" />
                        Add Trade
                      </Button>
                    </div>
                  ) : (
                    operationData
                      .filter(d => {
                        const date = new Date(d.date);
                        return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
                      })
                      .sort((a, b) => a.date.localeCompare(b.date))
                      .map((dayData) => {
                        const totalSetups = dayData.trades.length;
                        // BREAKEVEN counts as WIN
                        const totalWin = dayData.trades.filter(t => t.status === 'WIN' || t.status === 'BREAKEVEN').length;
                        const totalLoss = dayData.trades.filter(t => t.status === 'LOSS').length;
                        const totalPips = dayData.trades.reduce((sum, t) => sum + t.pips, 0);
                        
                        return (
                          <div key={dayData.date} className={`bg-[hsl(var(--surface-elevated))] rounded-xl overflow-hidden border border-[hsl(var(--border))] ${bulkMode && selectedOps.has(dayData.date) ? 'ring-2 ring-[#E63946]/40' : ''}`}>
                            {/* Date Header - Premium Styling */}
                            <div className="flex items-center justify-between px-5 py-3 bg-gradient-to-r from-[#3B82F6]/15 to-transparent border-b border-[hsl(var(--border))]">
                              <div className="flex items-center gap-5">
                                {bulkMode && (
                                  <input
                                    type="checkbox"
                                    className="cursor-pointer"
                                    checked={selectedOps.has(dayData.date)}
                                    onChange={() => toggleSelect(setSelectedOps, selectedOps, dayData.date)}
                                    data-testid={`bulk-ops-day-${dayData.date}`}
                                    aria-label={`Select ${dayData.date}`}
                                  />
                                )}
                                <div className="flex items-center gap-2">
                                  <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#3B82F6]/20 border border-[#3B82F6]/40">
                                    <Calendar size={16} className="text-[#3B82F6]" />
                                  </div>
                                  <span className="text-sm font-bold text-white">
                                    {new Date(dayData.date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                                  </span>
                                </div>
                                <div className="flex items-center gap-4 text-sm">
                                  <span className="text-[#9CA3AF]">Setups: <span className="text-white font-semibold">{totalSetups}</span></span>
                                  <span className="text-[#10B981]">Win: <span className="font-semibold">{totalWin}</span></span>
                                  <span className="text-[#E63946]">Loss: <span className="font-semibold">{totalLoss}</span></span>
                                  <span className="text-[#3B82F6]">Win Rate: <span className="font-semibold">{totalSetups > 0 ? ((totalWin / totalSetups) * 100).toFixed(1) : '0.0'}%</span></span>
                                  <span className={`font-semibold ${totalPips >= 0 ? 'text-[#10B981]' : 'text-[#E63946]'}`}>
                                    Pips: {totalPips >= 0 ? '+' : ''}{totalPips}
                                  </span>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 text-sm text-[#10B981] hover:bg-[#10B981]/10"
                                onClick={() => handleShareRow(dayData, 'operation')}
                                title="Share / Copy"
                              >
                                <Share2 size={14} className="mr-1" />
                                Share
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 text-sm text-[#3B82F6] hover:bg-[#3B82F6]/10"
                                onClick={() => {
                                  setNewOperationEntry({ ...newOperationEntry, date: dayData.date });
                                  setShowCreateModal(true);
                                }}
                              >
                                <PlusCircle size={16} className="mr-1.5" />
                                Add Trade
                              </Button>
                              </div>
                            </div>
                            
                            {/* Trades Table */}
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-b border-[hsl(var(--border))] bg-[hsl(var(--surface))]">
                                  <th className="text-left py-3 px-5 text-[#6B7280] font-semibold text-xs">Pair</th>
                                  <th className="text-center py-3 px-4 text-[#6B7280] font-semibold text-xs">Position</th>
                                  <th className="text-center py-3 px-4 text-[#6B7280] font-semibold text-xs">Channel</th>
                                  <th className="text-center py-3 px-4 text-[#6B7280] font-semibold text-xs">Signal</th>
                                  <th className="text-center py-3 px-4 text-[#6B7280] font-semibold text-xs">Signal Provider</th>
                                  <th className="text-center py-3 px-4 text-[#6B7280] font-semibold text-xs">Status</th>
                                  <th className="text-right py-3 px-4 text-[#6B7280] font-semibold text-xs">Pips</th>
                                  <th className="text-center py-3 px-5 text-[#6B7280] font-semibold text-xs w-24">Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {dayData.trades.map((trade) => (
                                  <tr key={trade.id} className="border-b border-[hsl(var(--border))]/50 hover:bg-white/5 transition-colors">
                                    <td className="py-3 px-5 text-white font-semibold">{trade.pair}</td>
                                    <td className="py-3 px-4 text-center">
                                      <Badge className={`text-xs px-2.5 py-1 ${trade.position === 'BUY' ? 'bg-[#10B981]/20 text-[#10B981]' : 'bg-[#E63946]/20 text-[#E63946]'}`}>
                                        {trade.position === 'BUY' ? <ArrowUpRight size={12} className="mr-1" /> : <ArrowDownRight size={12} className="mr-1" />}
                                        {trade.position}
                                      </Badge>
                                    </td>
                                    <td className="py-3 px-4 text-center">
                                      <Badge className={`text-xs px-2.5 py-1 ${
                                        trade.channel === 'public' ? 'bg-[#3B82F6]/20 text-[#3B82F6]' : 
                                        trade.channel === 'vip' ? 'bg-[#F59E0B]/20 text-[#F59E0B]' :
                                        'bg-[#8B5CF6]/20 text-[#8B5CF6]'
                                      }`}>
                                        {trade.channel === 'public' ? 'Public' : trade.channel === 'vip' ? 'VIP' : 'Both'}
                                      </Badge>
                                    </td>
                                    <td className="py-3 px-4 text-center">
                                      {trade.signal_generated ? (
                                        <Badge className="text-xs px-2.5 py-1 bg-[#06B6D4]/20 text-[#06B6D4]">
                                          {trade.signal_generated === 'livetrade' ? 'Livetrade' : trade.signal_generated === 'mapping' ? 'Mapping' : 'Others'}
                                        </Badge>
                                      ) : <span className="text-[#6B7280]">—</span>}
                                    </td>
                                    <td className="py-3 px-4 text-center">
                                      <ProviderBadge provider={trade.signal_provider} testid={`op-provider-${dayData.date}-${trade.id}`} />
                                    </td>
                                    <td className="py-3 px-4 text-center">
                                      <Badge className={`text-xs px-2.5 py-1 ${
                                        trade.status === 'WIN' ? 'bg-[#10B981]/20 text-[#10B981]' : 
                                        trade.status === 'BREAKEVEN' ? 'bg-[#F59E0B]/20 text-[#F59E0B]' :
                                        trade.status === 'LOSS' ? 'bg-[#E63946]/20 text-[#E63946]' : 
                                        'bg-[#6B7280]/20 text-[#6B7280]'
                                      }`}>
                                        {trade.status === 'BREAKEVEN' ? 'BE (Win)' : trade.status}
                                      </Badge>
                                    </td>
                                    <td className={`py-3 px-4 text-right font-bold text-sm ${trade.pips >= 0 ? 'text-[#10B981]' : 'text-[#E63946]'}`}>
                                      {trade.pips >= 0 ? '+' : ''}{trade.pips}
                                    </td>
                                    <td className="py-3 px-5 text-center">
                                      <div className="flex items-center justify-center gap-1">
                                        <Button 
                                          variant="ghost" 
                                          size="sm" 
                                          className="h-7 w-7 p-0 text-[#3B82F6]"
                                          onClick={() => { 
                                            setEditingEntry({ ...trade, date: dayData.date, type: 'operation' }); 
                                            setShowEditModal(true); 
                                          }}
                                        >
                                          <Edit2 size={14} />
                                        </Button>
                                        <Button 
                                          variant="ghost" 
                                          size="sm" 
                                          className="h-7 w-7 p-0 text-[#E63946]"
                                          onClick={() => confirmDelete(trade.id, dayData.date, trade)}
                                        >
                                          <Trash2 size={14} />
                                        </Button>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        );
                      })
                  )}
                  {/* TOTAL summary card — month aggregate for Operation */}
                  {operationData.filter(d => {
                    const date = new Date(d.date);
                    return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
                  }).length > 0 && (() => {
                    const monthDays = operationData.filter(d => {
                      const date = new Date(d.date);
                      return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
                    });
                    const allTrades = monthDays.flatMap(d => d.trades || []);
                    const totSetups = allTrades.length;
                    const totWin = allTrades.filter(t => t.status === 'WIN' || t.status === 'BREAKEVEN').length;
                    const totLoss = allTrades.filter(t => t.status === 'LOSS').length;
                    const totPips = allTrades.reduce((s, t) => s + (Number(t.pips) || 0), 0);
                    const winRate = totSetups > 0 ? (totWin / totSetups * 100) : 0;
                    return (
                      <div className="rounded-xl border-2 border-[#3B82F6]/40 bg-[#3B82F6]/8 px-5 py-4" data-testid="operation-total-row">
                        <div className="flex items-center justify-between flex-wrap gap-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#3B82F6]/20 border border-[#3B82F6]/40">
                              <Target size={16} className="text-[#3B82F6]" />
                            </div>
                            <span className="text-sm font-bold uppercase tracking-wider text-[#3B82F6]">Month Total</span>
                            <span className="text-xs text-[#6B7280]">— {monthNames[currentMonth]} {currentYear}</span>
                          </div>
                          <div className="flex items-center gap-5 text-sm flex-wrap">
                            <span className="text-[#9CA3AF]">Setups: <span className="text-white font-bold">{totSetups}</span></span>
                            <span className="text-[#10B981]">Win: <span className="font-bold">{totWin}</span></span>
                            <span className="text-[#E63946]">Loss: <span className="font-bold">{totLoss}</span></span>
                            <span className="text-[#3B82F6]">Win Rate: <span className="font-bold">{winRate.toFixed(1)}%</span></span>
                            <span className={`font-bold ${totPips >= 0 ? 'text-[#10B981]' : 'text-[#E63946]'}`}>
                              Pips: {totPips >= 0 ? '+' : ''}{totPips}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </TabsContent>
          </Tabs>

        </div>
      </div>

      {/* Create Entry Modal - Premium Design */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-[#0a0a0c] border-[hsl(var(--border))] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="pb-4 border-b border-[hsl(var(--border))]">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                activeTab === 'marketing' ? 'bg-gradient-to-br from-[#E63946]/30 to-[#F97316]/20 border border-[#E63946]/40' :
                activeTab === 'sales' ? 'bg-gradient-to-br from-[#10B981]/30 to-[#3B82F6]/20 border border-[#10B981]/40' :
                'bg-gradient-to-br from-[#3B82F6]/30 to-[#8B5CF6]/20 border border-[#3B82F6]/40'
              }`}>
                {activeTab === 'marketing' && <TrendingUp size={22} className="text-[#E63946]" />}
                {activeTab === 'sales' && <DollarSign size={22} className="text-[#10B981]" />}
                {activeTab === 'operation' && <Target size={22} className="text-[#3B82F6]" />}
              </div>
              <div>
                <DialogTitle className="text-lg font-bold text-white">
                  Add {activeTab === 'marketing' ? 'Marketing' : activeTab === 'sales' ? 'Sales' : 'Operation'} Data
                </DialogTitle>
                <p className="text-xs text-[#6B7280] mt-0.5">
                  {activeTab === 'marketing' && 'Track daily ad spending, impressions & conversions'}
                  {activeTab === 'sales' && 'Record lead funnel progress & broker performance'}
                  {activeTab === 'operation' && 'Log trade setups with pair, position & results'}
                </p>
              </div>
            </div>
          </DialogHeader>
          
          {activeTab === 'marketing' && (
            <div className="space-y-6 py-4">
              {/* Date with premium styling */}
              <div className="bg-[hsl(var(--surface-elevated))] p-4 rounded-xl border border-[hsl(var(--border))]">
                <div className="flex items-center gap-2 mb-2">
                  <Calendar size={14} className="text-[#E63946]" />
                  <Label className="text-[#9CA3AF] text-xs font-medium">Data Date</Label>
                </div>
                <Input
                  type="date"
                  value={newMarketingEntry.date}
                  onChange={(e) => setNewMarketingEntry({ ...newMarketingEntry, date: e.target.value })}
                  className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white [color-scheme:dark]"
                  data-testid="marketing-entry-date"
                />
              </div>
              
              {/* Spending by Platform */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-1 h-4 rounded-full bg-gradient-to-b from-[#10B981] to-[#10B981]/50" />
                  <p className="text-sm font-semibold text-white">Spending by Platform</p>
                  <span className="text-[10px] text-[#6B7280] ml-auto">RM per platform</span>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {Object.entries(platforms).map(([key, config]) => {
                    const IconComponent = config.icon;
                    return (
                      <div key={key} className="flex items-center gap-2 bg-[hsl(var(--surface-elevated))] p-2.5 rounded-lg border border-[hsl(var(--border))] hover:border-[hsl(var(--border-hover))] transition-colors">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${config.color}15` }}>
                          {typeof IconComponent === 'function' && IconComponent.prototype?.render ? 
                            <IconComponent size={16} style={{ color: config.color }} /> : 
                            <IconComponent />
                          }
                        </div>
                        <div className="flex-1 min-w-0">
                          <label className="text-[10px] text-[#6B7280] block">{config.label}</label>
                          <Input
                            type="number"
                            placeholder="0"
                            value={newMarketingEntry.spend[key] || ''}
                            onChange={(e) => setNewMarketingEntry({
                              ...newMarketingEntry,
                              spend: { ...newMarketingEntry.spend, [key]: parseFloat(e.target.value) || 0 }
                            })}
                            className="h-7 bg-transparent border-0 p-0 text-sm text-white font-mono focus-visible:ring-0"
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              {/* Impressions by Platform */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-1 h-4 rounded-full bg-gradient-to-b from-[#3B82F6] to-[#3B82F6]/50" />
                  <p className="text-sm font-semibold text-white">Impressions by Platform</p>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {Object.entries(platforms).map(([key, config]) => {
                    const IconComponent = config.icon;
                    return (
                      <div key={key} className="flex items-center gap-2 bg-[hsl(var(--surface-elevated))] p-2.5 rounded-lg border border-[hsl(var(--border))] hover:border-[hsl(var(--border-hover))] transition-colors">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${config.color}15` }}>
                          {typeof IconComponent === 'function' && IconComponent.prototype?.render ? 
                            <IconComponent size={16} style={{ color: config.color }} /> : 
                            <IconComponent />
                          }
                        </div>
                        <div className="flex-1 min-w-0">
                          <label className="text-[10px] text-[#6B7280] block">{config.label}</label>
                          <Input
                            type="number"
                            placeholder="0"
                            value={newMarketingEntry.impressions[key] || ''}
                            onChange={(e) => setNewMarketingEntry({
                              ...newMarketingEntry,
                              impressions: { ...newMarketingEntry.impressions, [key]: parseFloat(e.target.value) || 0 }
                            })}
                            className="h-7 bg-transparent border-0 p-0 text-sm text-white font-mono focus-visible:ring-0"
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              {/* Clicks by Platform */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-1 h-4 rounded-full bg-gradient-to-b from-[#F59E0B] to-[#F59E0B]/50" />
                  <p className="text-sm font-semibold text-white">Clicks by Platform</p>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {Object.entries(platforms).map(([key, config]) => {
                    const IconComponent = config.icon;
                    return (
                      <div key={key} className="flex items-center gap-2 bg-[hsl(var(--surface-elevated))] p-2.5 rounded-lg border border-[hsl(var(--border))] hover:border-[hsl(var(--border-hover))] transition-colors">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${config.color}15` }}>
                          {typeof IconComponent === 'function' && IconComponent.prototype?.render ? 
                            <IconComponent size={16} style={{ color: config.color }} /> : 
                            <IconComponent />
                          }
                        </div>
                        <div className="flex-1 min-w-0">
                          <label className="text-[10px] text-[#6B7280] block">{config.label}</label>
                          <Input
                            type="number"
                            placeholder="0"
                            value={newMarketingEntry.clicks[key] || ''}
                            onChange={(e) => setNewMarketingEntry({
                              ...newMarketingEntry,
                              clicks: { ...newMarketingEntry.clicks, [key]: parseFloat(e.target.value) || 0 }
                            })}
                            className="h-7 bg-transparent border-0 p-0 text-sm text-white font-mono focus-visible:ring-0"
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              {/* Other Metrics */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-1 h-4 rounded-full bg-gradient-to-b from-[#8B5CF6] to-[#8B5CF6]/50" />
                  <p className="text-sm font-semibold text-white">Conversion Metrics</p>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { key: 'lpv', label: 'Landing Page View', icon: Eye },
                    { key: 'cta', label: 'CTA Clicks', icon: Target },
                    { key: 'subsFreeTrial', label: 'Subs Free Trial', icon: Users },
                    { key: 'subsTG', label: 'Subs Telegram', icon: MessageCircle },
                    { key: 'subsBot', label: 'Subs Bot', icon: Settings },
                    { key: 'leads', label: 'Leads', icon: Users },
                    { key: 'closed', label: 'Closed', icon: Check },
                  ].map((metric) => (
                    <div key={metric.key} className="bg-[hsl(var(--surface-elevated))] p-2.5 rounded-lg border border-[hsl(var(--border))]">
                      <div className="flex items-center gap-2 mb-1">
                        <metric.icon size={12} className="text-[#8B5CF6]" />
                        <Label className="text-[10px] text-[#6B7280]">{metric.label}</Label>
                      </div>
                      <Input
                        type="number"
                        placeholder="0"
                        value={newMarketingEntry[metric.key] || ''}
                        onChange={(e) => setNewMarketingEntry({
                          ...newMarketingEntry,
                          [metric.key]: parseFloat(e.target.value) || 0
                        })}
                        className="h-8 bg-transparent border-[hsl(var(--border))] text-white font-mono"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'sales' && (
            <div className="space-y-6 py-4">
              {/* Date with premium styling */}
              <div className="bg-[hsl(var(--surface-elevated))] p-4 rounded-xl border border-[hsl(var(--border))]">
                <div className="flex items-center gap-2 mb-2">
                  <Calendar size={14} className="text-[#10B981]" />
                  <Label className="text-[#9CA3AF] text-xs font-medium">Data Date</Label>
                </div>
                <Input
                  type="date"
                  value={newSalesEntry.date}
                  onChange={(e) => setNewSalesEntry({ ...newSalesEntry, date: e.target.value })}
                  className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white [color-scheme:dark]"
                  data-testid="sales-entry-date"
                />
              </div>

              {/* Lead Funnel Section */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-1 h-4 rounded-full bg-gradient-to-b from-[#10B981] to-[#10B981]/50" />
                  <p className="text-sm font-semibold text-white">Lead Funnel</p>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { key: 'greeting', label: 'Greeting', icon: Hand, color: '#3B82F6' },
                    { key: 'oneTick', label: '1 Tick', icon: CheckCircle, color: '#8B5CF6' },
                    { key: 'registration', label: 'Registration', icon: ClipboardList, color: '#F59E0B' },
                    { key: 'broker', label: 'Broker', icon: Building2, color: '#10B981' },
                    { key: 'priceOffer', label: 'Price Offer', icon: Tag, color: '#E63946' },
                    { key: 'others', label: 'Others', icon: FileQuestion, color: '#6B7280' },
                  ].map((stage) => (
                    <div key={stage.key} className="bg-[hsl(var(--surface-elevated))] p-3 rounded-lg border border-[hsl(var(--border))]">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ backgroundColor: `${stage.color}20` }}>
                          <stage.icon size={12} style={{ color: stage.color }} />
                        </div>
                        <label className="text-[10px] text-[#6B7280]">{stage.label}</label>
                      </div>
                      <Input
                        type="number"
                        placeholder="0"
                        value={newSalesEntry[stage.key] || ''}
                        onChange={(e) => setNewSalesEntry({
                          ...newSalesEntry,
                          [stage.key]: parseFloat(e.target.value) || 0
                        })}
                        className="h-8 bg-transparent border-[hsl(var(--border))] text-sm text-white font-mono"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Leads by Source Section - NEW */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-1 h-4 rounded-full bg-gradient-to-b from-[#F59E0B] to-[#F59E0B]/50" />
                  <p className="text-sm font-semibold text-white">Leads by Source</p>
                </div>
                
                {/* Leads */}
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-semibold text-[#10B981]">Leads</span>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { key: 'new', label: 'New Leads' },
                      { key: 'prospect', label: 'Prospect Leads' },
                      { key: 'vip', label: 'VIP Leads' },
                    ].map((field) => (
                      <div key={field.key} className="bg-[hsl(var(--surface-elevated))] p-2.5 rounded-lg border border-[hsl(var(--border))]">
                        <Label className="text-[10px] text-[#6B7280]">{field.label}</Label>
                        <Input
                          type="number"
                          placeholder="0"
                          value={newSalesEntry.leadsS?.[field.key] || ''}
                          onChange={(e) => setNewSalesEntry({
                            ...newSalesEntry,
                            leadsS: { ...newSalesEntry.leadsS, [field.key]: parseFloat(e.target.value) || 0 }
                          })}
                          className="h-8 bg-transparent border-[hsl(var(--border))] text-sm text-white mt-1"
                        />
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Closed - New Closed, Returning Closed, Total Closed */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-[hsl(var(--surface-elevated))] p-2.5 rounded-lg border border-[hsl(var(--border))]">
                    <Label className="text-[10px] text-[#3B82F6]">New Closed</Label>
                    <Input
                      type="number"
                      placeholder="0"
                      value={newSalesEntry.newClosed || ''}
                      onChange={(e) => setNewSalesEntry({ ...newSalesEntry, newClosed: parseFloat(e.target.value) || 0 })}
                      className="h-8 bg-transparent border-[hsl(var(--border))] text-sm text-white mt-1"
                    />
                  </div>
                  <div className="bg-[hsl(var(--surface-elevated))] p-2.5 rounded-lg border border-[hsl(var(--border))]">
                    <Label className="text-[10px] text-[#8B5CF6]">Returning Closed</Label>
                    <Input
                      type="number"
                      placeholder="0"
                      value={newSalesEntry.vipClosed || ''}
                      onChange={(e) => setNewSalesEntry({ ...newSalesEntry, vipClosed: parseFloat(e.target.value) || 0 })}
                      className="h-8 bg-transparent border-[hsl(var(--border))] text-sm text-white mt-1"
                    />
                  </div>
                  <div className="bg-[hsl(var(--surface-elevated))] p-2.5 rounded-lg border border-[hsl(var(--border))]">
                    <Label className="text-[10px] text-[#10B981]">Total Closed</Label>
                    <div className="h-8 flex items-center justify-center text-sm font-semibold text-[#10B981] mt-1">
                      {(newSalesEntry.newClosed || 0) + (newSalesEntry.vipClosed || 0)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Broker Performance - Only show enabled brokers */}
              {enabledBrokers.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-1 h-4 rounded-full bg-gradient-to-b from-[#8B5CF6] to-[#8B5CF6]/50" />
                    <p className="text-sm font-semibold text-white">Broker Performance</p>
                  </div>
                  <div className="space-y-3">
                    {enabledBrokers.map(([brokerKey, config], idx) => (
                      <div key={brokerKey} className="bg-[hsl(var(--surface-elevated))] p-3 rounded-lg border border-[hsl(var(--border))]">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-[#10B981]/20 text-[#10B981] font-bold text-xs">
                            {String.fromCharCode(65 + idx)}
                          </div>
                          <span className="text-sm font-medium text-white">{config.name}</span>
                        </div>
                        <div className="grid grid-cols-4 gap-2">
                          {[
                            { key: 'active', label: 'Active' },
                            { key: 'lots', label: 'Lots' },
                            { key: 'commission', label: 'Commission (USD)' },
                            { key: 'deposit', label: 'Deposit (USD)' },
                          ].map((field) => (
                            <div key={field.key}>
                              <label className="text-[9px] text-[#6B7280] block mb-1">{field.label}</label>
                              <Input
                                type="number"
                                step={field.key === 'lots' ? '0.1' : '1'}
                                placeholder="0"
                                value={newSalesEntry[brokerKey]?.[field.key] || ''}
                                onChange={(e) => setNewSalesEntry({
                                  ...newSalesEntry,
                                  [brokerKey]: {
                                    ...newSalesEntry[brokerKey],
                                    [field.key]: field.key === 'lots' ? parseFloat(e.target.value) || 0 : parseFloat(e.target.value) || 0
                                  }
                                })}
                                className="h-7 bg-transparent border-[hsl(var(--border))] text-xs text-white font-mono"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'operation' && (
            <div className="space-y-5 py-4">
              {/* Date with premium styling */}
              <div className="bg-[hsl(var(--surface-elevated))] p-4 rounded-xl border border-[hsl(var(--border))]">
                <div className="flex items-center gap-2 mb-2">
                  <Calendar size={14} className="text-[#3B82F6]" />
                  <Label className="text-[#9CA3AF] text-xs font-medium">Trade Date</Label>
                </div>
                <Input
                  type="date"
                  value={newOperationEntry.date}
                  onChange={(e) => setNewOperationEntry({ ...newOperationEntry, date: e.target.value })}
                  className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white [color-scheme:dark]"
                  data-testid="operation-entry-date"
                />
              </div>
              
              {/* Trade Details */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-1 h-4 rounded-full bg-gradient-to-b from-[#3B82F6] to-[#3B82F6]/50" />
                  <p className="text-sm font-semibold text-white">Trade Details</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-[hsl(var(--surface-elevated))] p-3 rounded-lg border border-[hsl(var(--border))]">
                    <Label className="text-[10px] text-[#6B7280] block mb-2">Currency Pair</Label>
                    <Select value={newOperationEntry.pair} onValueChange={(v) => setNewOperationEntry({ ...newOperationEntry, pair: v })}>
                      <SelectTrigger className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]">
                        {forexPairs.map(pair => (
                          <SelectItem key={pair} value={pair} className="text-white">{pair}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="bg-[hsl(var(--surface-elevated))] p-3 rounded-lg border border-[hsl(var(--border))]">
                    <Label className="text-[10px] text-[#6B7280] block mb-2">Position</Label>
                    <Select value={newOperationEntry.position} onValueChange={(v) => setNewOperationEntry({ ...newOperationEntry, position: v })}>
                      <SelectTrigger className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]">
                        {positions.map(pos => (
                          <SelectItem key={pos} value={pos} className="text-white">
                            <div className="flex items-center gap-2">
                              {pos === 'BUY' ? <ArrowUpRight size={12} className="text-[#10B981]" /> : <ArrowDownRight size={12} className="text-[#E63946]" />}
                              {pos}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              
              {/* Channel Selection */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-1 h-4 rounded-full bg-gradient-to-b from-[#8B5CF6] to-[#8B5CF6]/50" />
                  <p className="text-sm font-semibold text-white">Channel</p>
                </div>
                <div className="bg-[hsl(var(--surface-elevated))] p-3 rounded-lg border border-[hsl(var(--border))]">
                  <Label className="text-[10px] text-[#6B7280] block mb-2">Post to Channel</Label>
                  <Select value={newOperationEntry.channel || 'both'} onValueChange={(v) => setNewOperationEntry({ ...newOperationEntry, channel: v })}>
                    <SelectTrigger className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]">
                      {channelOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value} className="text-white">
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full`} style={{ backgroundColor: opt.color }} />
                            {opt.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Signal Provider (wajib) */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-1 h-4 rounded-full bg-gradient-to-b from-[#EC4899] to-[#EC4899]/50" />
                  <p className="text-sm font-semibold text-white">Signal Provider <span className="text-[#E63946]">*</span></p>
                </div>
                <div className="bg-[hsl(var(--surface-elevated))] p-3 rounded-lg border border-[hsl(var(--border))]">
                  <Label className="text-[10px] text-[#6B7280] block mb-2">Siapa bagi signal ni</Label>
                  <ProviderSelect value={newOperationEntry.signal_provider} onChange={(v) => setNewOperationEntry({ ...newOperationEntry, signal_provider: v })} testid="new-op-provider-select" />
                  {!newOperationEntry.signal_provider && <p className="text-xs text-amber-400 mt-1.5" data-testid="new-op-provider-required">Wajib pilih sebelum simpan</p>}
                </div>
              </div>

              {/* Signal Generated */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-1 h-4 rounded-full bg-gradient-to-b from-[#06B6D4] to-[#06B6D4]/50" />
                  <p className="text-sm font-semibold text-white">Signal Generated</p>
                </div>
                <div className="bg-[hsl(var(--surface-elevated))] p-3 rounded-lg border border-[hsl(var(--border))]">
                  <Label className="text-[10px] text-[#6B7280] block mb-2">Source</Label>
                  <Select value={newOperationEntry.signal_generated || ''} onValueChange={(v) => setNewOperationEntry({ ...newOperationEntry, signal_generated: v })}>
                    <SelectTrigger className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white">
                      <SelectValue placeholder="Select source" />
                    </SelectTrigger>
                    <SelectContent className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]">
                      <SelectItem value="livetrade" className="text-white">Livetrade</SelectItem>
                      <SelectItem value="mapping" className="text-white">Mapping</SelectItem>
                      <SelectItem value="others" className="text-white">Others</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Result */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-1 h-4 rounded-full bg-gradient-to-b from-[#10B981] to-[#10B981]/50" />
                  <p className="text-sm font-semibold text-white">Trade Result</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-[hsl(var(--surface-elevated))] p-3 rounded-lg border border-[hsl(var(--border))]">
                    <Label className="text-[10px] text-[#6B7280] block mb-2">Status</Label>
                    <Select value={newOperationEntry.status} onValueChange={(v) => setNewOperationEntry({ ...newOperationEntry, status: v })}>
                      <SelectTrigger className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]">
                        {tradeStatuses.map(status => (
                          <SelectItem key={status} value={status} className="text-white">
                            <div className="flex items-center gap-2">
                              <div className={`w-2 h-2 rounded-full ${status === 'WIN' ? 'bg-[#10B981]' : status === 'LOSS' ? 'bg-[#E63946]' : 'bg-[#6B7280]'}`} />
                              {status}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="bg-[hsl(var(--surface-elevated))] p-3 rounded-lg border border-[hsl(var(--border))]">
                    <Label className="text-[10px] text-[#6B7280] block mb-2">Pips (+/-)</Label>
                    <Input
                      type="number"
                      value={newOperationEntry.pips}
                      onChange={(e) => setNewOperationEntry({ ...newOperationEntry, pips: e.target.value })}
                      placeholder="e.g. 45 or -15"
                      className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white font-mono"
                      data-testid="operation-entry-pips"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter className="pt-4 border-t border-[hsl(var(--border))]">
            <Button variant="outline" onClick={() => setShowCreateModal(false)} className="border-[hsl(var(--border))] text-white hover:bg-white/5">
              Cancel
            </Button>
            <Button
              onClick={handleCreateEntry}
              className={`text-white ${
                activeTab === 'marketing' ? 'bg-gradient-to-r from-[#E63946] to-[#F97316]' :
                activeTab === 'sales' ? 'bg-gradient-to-r from-[#10B981] to-[#3B82F6]' :
                'bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6]'
              }`}
              data-testid="save-daily-data-btn"
            >
              <Check size={16} className="mr-2" />
              Save Data
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Broker Configuration Modal */}
      <Dialog open={showBrokerConfig} onOpenChange={(open) => { if (!open) handleBrokerConfigSave(); else setShowBrokerConfig(true); }}>
        <DialogContent className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] max-w-md max-h-[85vh] overflow-y-auto" data-testid="broker-config-modal">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Settings size={20} className="text-[#10B981]" />
              Configure Brokers
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className="text-xs text-[#6B7280]">Enable and name the brokers used by this IB. Only enabled brokers will show in the table.</p>

            {/* Broker History — quick-pick from past data */}
            {brokerHistory.length > 0 && (
              <div className="bg-[#10B981]/5 border border-[#10B981]/20 rounded-lg p-3" data-testid="broker-history-panel">
                <div className="flex items-start gap-2 mb-2">
                  <Info size={14} className="text-[#10B981] flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-[#10B981]">
                    <div className="font-semibold mb-1">Broker yang pernah ada data dalam channel ni:</div>
                    <div className="text-[#6B7280] text-[11px]">Click untuk masukkan ke slot kosong. Jangan keluarkan slot yang ada data lama — biarkan walaupun bulan semasa tak guna.</div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {brokerHistory.map((bh) => {
                    const inConfig = Object.values(brokerConfig).some(
                      c => (c.name || '').toLowerCase() === bh.slug
                    );
                    return (
                      <button
                        key={bh.slug}
                        onClick={() => {
                          if (inConfig) {
                            toast.info(`${bh.display} dah ada dalam config`);
                            return;
                          }
                          // Find first empty slot
                          const slots = ['brokerA', 'brokerB', 'brokerC', 'brokerD', 'brokerE'];
                          const emptySlot = slots.find(s => !(brokerConfig[s]?.name || '').trim());
                          if (!emptySlot) {
                            toast.error('Semua 5 slot dah penuh. Sila kosongkan satu slot dulu.');
                            return;
                          }
                          setBrokerConfig({
                            ...brokerConfig,
                            [emptySlot]: { name: bh.display, enabled: true }
                          });
                          toast.success(`${bh.display} ditambah ke Slot ${emptySlot.slice(-1)}`);
                        }}
                        disabled={inConfig}
                        className={`px-2 py-1 text-[11px] rounded-md border transition ${
                          inConfig
                            ? 'bg-[#6B7280]/10 border-[#6B7280]/30 text-[#6B7280] cursor-not-allowed'
                            : 'bg-[#10B981]/10 border-[#10B981]/30 text-[#10B981] hover:bg-[#10B981]/20'
                        }`}
                        data-testid={`broker-history-pick-${bh.slug}`}
                        title={`${bh.entry_count} entries dalam ${bh.month_count} bulan${bh.last_date ? ` · last: ${bh.last_date}` : ''}`}
                      >
                        {bh.display}
                        <span className="ml-1 opacity-60">({bh.entry_count})</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            
            {Object.entries(brokerConfig).map(([key, config], idx) => {
              const slugInSlot = (config.name || '').toLowerCase().trim();
              const histEntry = brokerHistory.find(b => b.slug === slugInSlot);
              return (
              <div key={key} className="flex items-center gap-3 bg-[hsl(var(--surface-elevated))] p-3 rounded-lg border border-[hsl(var(--border))]">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#10B981]/20 text-[#10B981] font-bold text-sm">
                  {String.fromCharCode(65 + idx)}
                </div>
                <div className="flex-1">
                  <Input
                    placeholder={`Broker ${String.fromCharCode(65 + idx)} name`}
                    value={config.name}
                    onChange={(e) => {
                      const newName = e.target.value;
                      // Warn if user is clearing a slot that has historical data
                      if (histEntry && newName.trim() === '' && config.name.trim() !== '') {
                        toast.warning(
                          `⚠️ Slot ${String.fromCharCode(65 + idx)} (${config.name}) ada ${histEntry.entry_count} entries dalam ${histEntry.month_count} bulan. Biarkan slot ni — kalau awak buang, data history takkan nampak di Daily Data lagi.`,
                          { duration: 8000 }
                        );
                      }
                      setBrokerConfig({
                        ...brokerConfig,
                        [key]: { ...config, name: newName, enabled: newName.length > 0 }
                      });
                    }}
                    className="bg-transparent border-[hsl(var(--border))] text-white"
                    data-testid={`broker-config-input-${key}`}
                  />
                  {histEntry && !slotUsage.find(s => s.slot === key && s.entry_count > 0) && (
                    <div className="text-[10px] text-[#10B981]/70 mt-1">
                      📊 {histEntry.entry_count} entries dalam {histEntry.month_count} bulan
                    </div>
                  )}
                  {(() => {
                    const su = slotUsage.find(s => s.slot === key);
                    if (!su || su.entry_count === 0) return null;
                    return (
                      <div className="text-[10px] text-[#EF4444] mt-1 leading-snug" data-testid={`slot-legacy-warning-${key}`}>
                        ⚠️ Slot ni dah ada {su.entry_count} entries data lama ({su.month_count} bulan · ${su.commission_usd.toLocaleString()}).
                        Nama baru akan MELABELKAN data lama tu — guna slot yang betul-betul kosong, atau archive data slot ni dulu di Admin → Broker Cleanup.
                      </div>
                    );
                  })()}
                </div>
                <Badge className={config.enabled ? 'bg-[#10B981]/20 text-[#10B981]' : 'bg-[#6B7280]/20 text-[#6B7280]'}>
                  {config.enabled ? 'Active' : 'Inactive'}
                </Badge>
              </div>
              );
            })}
          </div>
          
          <DialogFooter>
            <Button onClick={handleBrokerConfigSave} className="bg-gradient-to-r from-[#10B981] to-[#3B82F6] text-white" data-testid="broker-config-save-btn">
              Save Configuration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal - Full fields matching Add Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="bg-[#0a0a0c] border-[hsl(var(--border))] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="pb-4 border-b border-[hsl(var(--border))]">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                editingEntry?.spend ? 'bg-gradient-to-br from-[#E63946]/30 to-[#F97316]/20 border border-[#E63946]/40' :
                editingEntry?.leadsM !== undefined ? 'bg-gradient-to-br from-[#10B981]/30 to-[#3B82F6]/20 border border-[#10B981]/40' :
                'bg-gradient-to-br from-[#3B82F6]/30 to-[#8B5CF6]/20 border border-[#3B82F6]/40'
              }`}>
                {editingEntry?.spend && <TrendingUp size={22} className="text-[#E63946]" />}
                {editingEntry?.leadsM !== undefined && <DollarSign size={22} className="text-[#10B981]" />}
                {editingEntry?.type === 'operation' && <Target size={22} className="text-[#3B82F6]" />}
              </div>
              <div>
                <DialogTitle className="text-lg font-bold text-white">
                  Edit {editingEntry?.spend ? 'Marketing' : editingEntry?.leadsM !== undefined ? 'Sales' : 'Operation'} Data
                </DialogTitle>
                <p className="text-xs text-[#6B7280] mt-0.5">Edit entry for {editingEntry?.date || ''}</p>
              </div>
            </div>
          </DialogHeader>
          
          {editingEntry && (
            <div className="space-y-6 py-4">
              {/* Date */}
              <div className="bg-[hsl(var(--surface-elevated))] p-4 rounded-xl border border-[hsl(var(--border))]">
                <div className="flex items-center gap-2 mb-2">
                  <Calendar size={14} className="text-[#E63946]" />
                  <Label className="text-[#9CA3AF] text-xs font-medium">Data Date</Label>
                </div>
                <Input
                  type="date"
                  value={editingEntry.date || ''}
                  onChange={(e) => setEditingEntry({ ...editingEntry, date: e.target.value })}
                  className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white [color-scheme:dark]"
                />
              </div>
              
              {/* Marketing Edit Fields - FULL */}
              {editingEntry.spend && (
                <>
                  {/* Spending by Platform */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-1 h-4 rounded-full bg-gradient-to-b from-[#10B981] to-[#10B981]/50" />
                      <p className="text-sm font-semibold text-white">Spending by Platform</p>
                      <span className="text-[10px] text-[#6B7280] ml-auto">RM per platform</span>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      {Object.entries(platforms).map(([key, config]) => {
                        const IconComponent = config.icon;
                        return (
                          <div key={key} className="flex items-center gap-2 bg-[hsl(var(--surface-elevated))] p-2.5 rounded-lg border border-[hsl(var(--border))]">
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${config.color}15` }}>
                              {typeof IconComponent === 'function' && IconComponent.prototype?.render ? 
                                <IconComponent size={16} style={{ color: config.color }} /> : 
                                <IconComponent />
                              }
                            </div>
                            <div className="flex-1 min-w-0">
                              <label className="text-[10px] text-[#6B7280] block">{config.label}</label>
                              <Input
                                type="number"
                                placeholder="0"
                                value={editingEntry.spend[key] || ''}
                                onChange={(e) => setEditingEntry({
                                  ...editingEntry,
                                  spend: { ...editingEntry.spend, [key]: parseFloat(e.target.value) || 0 }
                                })}
                                className="h-7 bg-transparent border-0 p-0 text-sm text-white font-mono focus-visible:ring-0"
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* Impressions by Platform */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-1 h-4 rounded-full bg-gradient-to-b from-[#3B82F6] to-[#3B82F6]/50" />
                      <p className="text-sm font-semibold text-white">Impressions by Platform</p>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      {Object.entries(platforms).map(([key, config]) => {
                        const IconComponent = config.icon;
                        return (
                          <div key={key} className="flex items-center gap-2 bg-[hsl(var(--surface-elevated))] p-2.5 rounded-lg border border-[hsl(var(--border))]">
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${config.color}15` }}>
                              {typeof IconComponent === 'function' && IconComponent.prototype?.render ? 
                                <IconComponent size={16} style={{ color: config.color }} /> : 
                                <IconComponent />
                              }
                            </div>
                            <div className="flex-1 min-w-0">
                              <label className="text-[10px] text-[#6B7280] block">{config.label}</label>
                              <Input
                                type="number"
                                placeholder="0"
                                value={editingEntry.impressions?.[key] || ''}
                                onChange={(e) => setEditingEntry({
                                  ...editingEntry,
                                  impressions: { ...editingEntry.impressions, [key]: parseFloat(e.target.value) || 0 }
                                })}
                                className="h-7 bg-transparent border-0 p-0 text-sm text-white font-mono focus-visible:ring-0"
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* Clicks by Platform */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-1 h-4 rounded-full bg-gradient-to-b from-[#F59E0B] to-[#F59E0B]/50" />
                      <p className="text-sm font-semibold text-white">Clicks by Platform</p>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      {Object.entries(platforms).map(([key, config]) => {
                        const IconComponent = config.icon;
                        return (
                          <div key={key} className="flex items-center gap-2 bg-[hsl(var(--surface-elevated))] p-2.5 rounded-lg border border-[hsl(var(--border))]">
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${config.color}15` }}>
                              {typeof IconComponent === 'function' && IconComponent.prototype?.render ? 
                                <IconComponent size={16} style={{ color: config.color }} /> : 
                                <IconComponent />
                              }
                            </div>
                            <div className="flex-1 min-w-0">
                              <label className="text-[10px] text-[#6B7280] block">{config.label}</label>
                              <Input
                                type="number"
                                placeholder="0"
                                value={editingEntry.clicks?.[key] || ''}
                                onChange={(e) => setEditingEntry({
                                  ...editingEntry,
                                  clicks: { ...editingEntry.clicks, [key]: parseFloat(e.target.value) || 0 }
                                })}
                                className="h-7 bg-transparent border-0 p-0 text-sm text-white font-mono focus-visible:ring-0"
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* Conversion Metrics */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-1 h-4 rounded-full bg-gradient-to-b from-[#8B5CF6] to-[#8B5CF6]/50" />
                      <p className="text-sm font-semibold text-white">Conversion Metrics</p>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      {[
                        { key: 'lpv', label: 'Landing Page View', icon: Eye },
                        { key: 'cta', label: 'CTA Clicks', icon: Target },
                        { key: 'subsFreeTrial', label: 'Subs Free Trial', icon: Users },
                        { key: 'subsTG', label: 'Subs Telegram', icon: MessageCircle },
                        { key: 'subsBot', label: 'Subs Bot', icon: Settings },
                        { key: 'leads', label: 'Leads', icon: Users },
                        { key: 'closed', label: 'Closed', icon: Check },
                      ].map((metric) => (
                        <div key={metric.key} className="bg-[hsl(var(--surface-elevated))] p-2.5 rounded-lg border border-[hsl(var(--border))]">
                          <div className="flex items-center gap-2 mb-1">
                            <metric.icon size={12} className="text-[#8B5CF6]" />
                            <Label className="text-[10px] text-[#6B7280]">{metric.label}</Label>
                          </div>
                          <Input
                            type="number"
                            placeholder="0"
                            value={editingEntry[metric.key] || ''}
                            onChange={(e) => setEditingEntry({
                              ...editingEntry,
                              [metric.key]: parseFloat(e.target.value) || 0
                            })}
                            className="h-8 bg-transparent border-[hsl(var(--border))] text-white font-mono"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
              
              {/* Sales Edit Fields - FULL */}
              {editingEntry.leadsM !== undefined && (
                <>
                  {/* Lead Funnel */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-1 h-4 rounded-full bg-gradient-to-b from-[#10B981] to-[#10B981]/50" />
                      <p className="text-sm font-semibold text-white">Lead Funnel</p>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      {[
                        { key: 'greeting', label: 'Greeting', icon: Hand, color: '#3B82F6' },
                        { key: 'oneTick', label: '1 Tick', icon: CheckCircle, color: '#8B5CF6' },
                        { key: 'registration', label: 'Registration', icon: ClipboardList, color: '#F59E0B' },
                        { key: 'broker', label: 'Broker', icon: Building2, color: '#10B981' },
                        { key: 'priceOffer', label: 'Price Offer', icon: Tag, color: '#E63946' },
                        { key: 'others', label: 'Others', icon: FileQuestion, color: '#6B7280' },
                      ].map((stage) => (
                        <div key={stage.key} className="bg-[hsl(var(--surface-elevated))] p-3 rounded-lg border border-[hsl(var(--border))]">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ backgroundColor: `${stage.color}20` }}>
                              <stage.icon size={12} style={{ color: stage.color }} />
                            </div>
                            <label className="text-[10px] text-[#6B7280]">{stage.label}</label>
                          </div>
                          <Input
                            type="number"
                            placeholder="0"
                            value={editingEntry[stage.key] || ''}
                            onChange={(e) => setEditingEntry({
                              ...editingEntry,
                              [stage.key]: parseFloat(e.target.value) || 0
                            })}
                            className="h-8 bg-transparent border-[hsl(var(--border))] text-sm text-white font-mono"
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Leads by Source */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-1 h-4 rounded-full bg-gradient-to-b from-[#F59E0B] to-[#F59E0B]/50" />
                      <p className="text-sm font-semibold text-white">Leads by Source</p>
                    </div>
                    
                    {/* Leads */}
                    <div className="mb-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs font-medium text-[#10B981]">Leads</span>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <div className="bg-[hsl(var(--surface-elevated))] p-2 rounded-lg border border-[hsl(var(--border))]">
                          <Label className="text-[10px] text-[#6B7280]">New</Label>
                          <Input type="number" step="any" value={editingEntry.leadsS?.new || ''} onChange={(e) => setEditingEntry({ ...editingEntry, leadsS: { ...editingEntry.leadsS, new: parseFloat(e.target.value) || 0 } })} className="h-8 bg-transparent border-[hsl(var(--border))] text-white mt-1" />
                        </div>
                        <div className="bg-[hsl(var(--surface-elevated))] p-2 rounded-lg border border-[hsl(var(--border))]">
                          <Label className="text-[10px] text-[#6B7280]">Prospect</Label>
                          <Input type="number" step="any" value={editingEntry.leadsS?.prospect || ''} onChange={(e) => setEditingEntry({ ...editingEntry, leadsS: { ...editingEntry.leadsS, prospect: parseFloat(e.target.value) || 0 } })} className="h-8 bg-transparent border-[hsl(var(--border))] text-white mt-1" />
                        </div>
                        <div className="bg-[hsl(var(--surface-elevated))] p-2 rounded-lg border border-[hsl(var(--border))]">
                          <Label className="text-[10px] text-[#6B7280]">VIP</Label>
                          <Input type="number" step="any" value={editingEntry.leadsS?.vip || ''} onChange={(e) => setEditingEntry({ ...editingEntry, leadsS: { ...editingEntry.leadsS, vip: parseFloat(e.target.value) || 0 } })} className="h-8 bg-transparent border-[hsl(var(--border))] text-white mt-1" />
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Closed - New Closed, Returning Closed, Total Closed */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-1 h-4 rounded-full bg-gradient-to-b from-[#8B5CF6] to-[#8B5CF6]/50" />
                      <p className="text-sm font-semibold text-white">Conversion</p>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="bg-[hsl(var(--surface-elevated))] p-3 rounded-lg border border-[hsl(var(--border))]">
                        <Label className="text-[10px] text-[#3B82F6]">New Closed</Label>
                        <Input type="number" step="any" value={editingEntry.newClosed || editingEntry.closed || ''} onChange={(e) => setEditingEntry({ ...editingEntry, newClosed: parseFloat(e.target.value) || 0 })} className="h-8 bg-transparent border-[hsl(var(--border))] text-white mt-1" />
                      </div>
                      <div className="bg-[hsl(var(--surface-elevated))] p-3 rounded-lg border border-[hsl(var(--border))]">
                        <Label className="text-[10px] text-[#8B5CF6]">Returning Closed</Label>
                        <Input type="number" step="any" value={editingEntry.vipClosed || ''} onChange={(e) => setEditingEntry({ ...editingEntry, vipClosed: parseFloat(e.target.value) || 0 })} className="h-8 bg-transparent border-[hsl(var(--border))] text-white mt-1" />
                      </div>
                      <div className="bg-[hsl(var(--surface-elevated))] p-3 rounded-lg border border-[hsl(var(--border))]">
                        <Label className="text-[10px] text-[#10B981]">Total Closed</Label>
                        <div className="h-8 flex items-center text-lg font-semibold text-[#10B981] mt-1">
                          {(editingEntry.newClosed || editingEntry.closed || 0) + (editingEntry.vipClosed || 0)}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Broker Performance - always show if brokers configured */}
                  {enabledBrokers.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-1 h-4 rounded-full bg-gradient-to-b from-[#E63946] to-[#E63946]/50" />
                        <p className="text-sm font-semibold text-white">Broker Performance</p>
                      </div>
                      {Object.entries(brokerConfig).filter(([_, cfg]) => cfg.enabled && cfg.name).map(([key, cfg]) => (
                        <div key={key} className="mb-3 p-3 bg-[hsl(var(--surface-elevated))] rounded-lg border border-[hsl(var(--border))]">
                          <p className="text-xs font-semibold text-[#F59E0B] mb-2">{cfg.name}</p>
                          <div className="grid grid-cols-4 gap-2">
                            <div>
                              <Label className="text-[10px] text-[#6B7280]">Active</Label>
                              <Input type="number" step="any" value={editingEntry[key]?.active ?? ''} onChange={(e) => setEditingEntry({ ...editingEntry, [key]: { ...editingEntry[key], active: parseFloat(e.target.value) || 0 } })} className="h-7 bg-transparent border-[hsl(var(--border))] text-white text-xs mt-1" />
                            </div>
                            <div>
                              <Label className="text-[10px] text-[#6B7280]">Lots</Label>
                              <Input type="number" step="any" value={editingEntry[key]?.lots ?? ''} onChange={(e) => setEditingEntry({ ...editingEntry, [key]: { ...editingEntry[key], lots: parseFloat(e.target.value) || 0 } })} className="h-7 bg-transparent border-[hsl(var(--border))] text-white text-xs mt-1" />
                            </div>
                            <div>
                              <Label className="text-[10px] text-[#6B7280]">Commission (USD)</Label>
                              <Input type="number" step="any" value={editingEntry[key]?.commission ?? ''} onChange={(e) => setEditingEntry({ ...editingEntry, [key]: { ...editingEntry[key], commission: parseFloat(e.target.value) || 0 } })} className="h-7 bg-transparent border-[hsl(var(--border))] text-white text-xs mt-1" />
                            </div>
                            <div>
                              <Label className="text-[10px] text-[#6B7280]">Deposit (USD)</Label>
                              <Input type="number" step="any" value={editingEntry[key]?.deposit ?? ''} onChange={(e) => setEditingEntry({ ...editingEntry, [key]: { ...editingEntry[key], deposit: parseFloat(e.target.value) || 0 } })} className="h-7 bg-transparent border-[hsl(var(--border))] text-white text-xs mt-1" />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
              
              {/* Operation Edit Fields */}
              {editingEntry.type === 'operation' && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-[hsl(var(--surface-elevated))] p-3 rounded-lg border border-[hsl(var(--border))]">
                      <Label className="text-[10px] text-[#6B7280]">Pair</Label>
                      <Input 
                        type="text" 
                        value={editingEntry.pair || ''} 
                        onChange={(e) => setEditingEntry({ ...editingEntry, pair: e.target.value.toUpperCase() })} 
                        className="bg-transparent border-[hsl(var(--border))] text-white mt-1" 
                        placeholder="e.g. XAUUSD"
                      />
                    </div>
                    <div className="bg-[hsl(var(--surface-elevated))] p-3 rounded-lg border border-[hsl(var(--border))]">
                      <Label className="text-[10px] text-[#6B7280]">Position</Label>
                      <select 
                        value={editingEntry.position || 'BUY'} 
                        onChange={(e) => setEditingEntry({ ...editingEntry, position: e.target.value })}
                        className="w-full h-9 bg-transparent border border-[hsl(var(--border))] text-white rounded-md mt-1 px-2"
                      >
                        <option value="BUY" className="bg-[#1a1a2e]">BUY</option>
                        <option value="SELL" className="bg-[#1a1a2e]">SELL</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-[hsl(var(--surface-elevated))] p-3 rounded-lg border border-[hsl(var(--border))]">
                      <Label className="text-[10px] text-[#6B7280]">Status</Label>
                      <select 
                        value={editingEntry.status || 'WIN'} 
                        onChange={(e) => setEditingEntry({ ...editingEntry, status: e.target.value })}
                        className="w-full h-9 bg-transparent border border-[hsl(var(--border))] text-white rounded-md mt-1 px-2"
                      >
                        <option value="WIN" className="bg-[#1a1a2e]">WIN</option>
                        <option value="LOSS" className="bg-[#1a1a2e]">LOSS</option>
                        <option value="BREAKEVEN" className="bg-[#1a1a2e]">BREAKEVEN</option>
                      </select>
                    </div>
                    <div className="bg-[hsl(var(--surface-elevated))] p-3 rounded-lg border border-[hsl(var(--border))]">
                      <Label className="text-[10px] text-[#6B7280]">Pips</Label>
                      <Input 
                        type="number" 
                        value={editingEntry.pips || ''} 
                        onChange={(e) => setEditingEntry({ ...editingEntry, pips: parseFloat(e.target.value) || 0 })} 
                        className="bg-transparent border-[hsl(var(--border))] text-white mt-1" 
                      />
                    </div>
                  </div>
                  
                  {/* Channel Selection */}
                  <div className="bg-[hsl(var(--surface-elevated))] p-3 rounded-lg border border-[hsl(var(--border))]">
                    <Label className="text-[10px] text-[#6B7280]">Channel</Label>
                    <select 
                      value={editingEntry.channel || 'both'} 
                      onChange={(e) => setEditingEntry({ ...editingEntry, channel: e.target.value })}
                      className="w-full h-9 bg-transparent border border-[hsl(var(--border))] text-white rounded-md mt-1 px-2"
                    >
                      <option value="public" className="bg-[#1a1a2e]">Public</option>
                      <option value="vip" className="bg-[#1a1a2e]">VIP</option>
                      <option value="both" className="bg-[#1a1a2e]">Both</option>
                    </select>
                  </div>
                  
                  {/* Signal Generated */}
                  <div className="bg-[hsl(var(--surface-elevated))] p-3 rounded-lg border border-[hsl(var(--border))]">
                    <Label className="text-[10px] text-[#6B7280]">Signal Generated</Label>
                    <select 
                      value={editingEntry.signal_generated || ''} 
                      onChange={(e) => setEditingEntry({ ...editingEntry, signal_generated: e.target.value })}
                      className="w-full h-9 bg-transparent border border-[hsl(var(--border))] text-white rounded-md mt-1 px-2"
                    >
                      <option value="" className="bg-[#1a1a2e]">— Select —</option>
                      <option value="livetrade" className="bg-[#1a1a2e]">Livetrade</option>
                      <option value="mapping" className="bg-[#1a1a2e]">Mapping</option>
                      <option value="others" className="bg-[#1a1a2e]">Others</option>
                    </select>
                  </div>

                  {/* Signal Provider */}
                  <div className="bg-[hsl(var(--surface-elevated))] p-3 rounded-lg border border-[hsl(var(--border))]">
                    <Label className="text-[10px] text-[#6B7280]">Signal Provider</Label>
                    <ProviderSelect value={editingEntry.signal_provider} allowUnset className="mt-1" onChange={(v) => setEditingEntry({ ...editingEntry, signal_provider: v })} testid="edit-op-provider-select" />
                  </div>
                </>
              )}
            </div>
          )}
          
          <DialogFooter className="border-t border-[hsl(var(--border))] pt-4">
            <Button variant="outline" onClick={() => setShowEditModal(false)} className="border-[hsl(var(--border))] text-white">
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (editingEntry) {
                  // ── Index-immune save: use _sourceRef (captured at edit-open) to find the EXACT row,
                  // splice by INDEX. This avoids id-collision bugs when entries share date-based ids.
                  const sourceRef = editingEntry._sourceRef;
                  // Strip transient bookkeeping fields before persisting
                  const cleanEntry = { ...editingEntry };
                  delete cleanEntry._sourceRef;

                  let newMkt = marketingData, newSales = salesData, newOps = operationData;
                  if (editingEntry.spend) {
                    const idx = sourceRef ? marketingData.indexOf(sourceRef) : marketingData.findIndex(r => r.id === editingEntry.id);
                    if (idx >= 0) {
                      newMkt = marketingData.slice();
                      newMkt[idx] = cleanEntry;
                    } else {
                      newMkt = marketingData.map(item => item.id === editingEntry.id ? cleanEntry : item);
                    }
                    setMarketingData(newMkt);
                  } else if (editingEntry.leadsM !== undefined) {
                    // Sync brokerA/B/C edits back to brokers dict.
                    // CRITICAL: use SAME activeBrokers ordering as the read-time mapping
                    // (line ~301) — otherwise edits get scrambled into wrong broker keys
                    // and existing brokers outside the display range silently get lost.
                    const syncedEntry = { ...cleanEntry };
                    const activeBrokers = ib.active_brokers || ['octa', 'hfm', 'vantage'];
                    const abcKeys = ['brokerA', 'brokerB', 'brokerC', 'brokerD', 'brokerE'];
                    // Preserve every existing broker (including ones not in the display)
                    const updatedBrokers = { ...(syncedEntry.brokers || {}) };
                    activeBrokers.forEach((bname, idx) => {
                      if (idx < abcKeys.length && syncedEntry[abcKeys[idx]]) {
                        const existing = updatedBrokers[bname] || {};
                        const rate = syncedEntry.exchange_rate_used || 4.2;
                        const comm = syncedEntry[abcKeys[idx]].commission || 0;
                        const dep = syncedEntry[abcKeys[idx]].deposit || 0;
                        updatedBrokers[bname] = {
                          ...existing,
                          active: syncedEntry[abcKeys[idx]].active || 0,
                          lots: syncedEntry[abcKeys[idx]].lots || 0,
                          commission: comm,
                          commission_usd: comm,
                          commission_myr: comm * rate,
                          deposit: dep,
                          deposit_usd: dep,
                          deposit_myr: dep * rate,
                        };
                      }
                    });
                    syncedEntry.brokers = updatedBrokers;
                    warnIfOutlier(syncedEntry);
                    const idx = sourceRef ? salesData.indexOf(sourceRef) : salesData.findIndex(r => r.id === editingEntry.id);
                    if (idx >= 0) {
                      newSales = salesData.slice();
                      newSales[idx] = syncedEntry;
                    } else {
                      newSales = salesData.map(item => item.id === syncedEntry.id ? syncedEntry : item);
                    }
                    setSalesData(newSales);
                  } else if (editingEntry.type === 'operation') {
                    newOps = operationData.map(dayData => {
                      if (dayData.date === editingEntry.date) {
                        return {
                          ...dayData,
                          trades: dayData.trades.map(trade => 
                            trade.id === editingEntry.id 
                              ? { ...trade, pair: editingEntry.pair, position: editingEntry.position, status: editingEntry.status, pips: editingEntry.pips, channel: editingEntry.channel || 'both', signal_generated: editingEntry.signal_generated || '', signal_provider: editingEntry.signal_provider || '' }
                              : trade
                          )
                        };
                      }
                      return dayData;
                    });
                    setOperationData(newOps);
                  }
                  // EXPLICIT SAVE to MongoDB
                  saveCurrentData(newMkt, newSales, newOps);
                  setShowEditModal(false);
                  const tabName = editingEntry.spend ? 'Marketing' : editingEntry.leadsM !== undefined ? 'Sales' : 'Operation';
                  logEditAction('EDIT', tabName, `Edited ${tabName.toLowerCase()} entry for ${editingEntry.date || 'entry'}`);
                  setEditingEntry(null);
                }
              }}
              className="bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] text-white"
            >
              <Check size={16} className="mr-2" />
              Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Data Modal */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent className="bg-[#0a0a0c] border-[hsl(var(--border))] max-w-lg">
          <DialogHeader className="pb-4 border-b border-[hsl(var(--border))]">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#10B981]/30 to-[#3B82F6]/20 border border-[#10B981]/40 flex items-center justify-center">
                <Upload size={22} className="text-[#10B981]" />
              </div>
              <div>
                <DialogTitle className="text-lg font-bold text-white">Import Data dari Fail</DialogTitle>
                <p className="text-xs text-[#6B7280] mt-0.5">Upload fail CSV atau Excel (.xlsx) untuk import data bulanan</p>
              </div>
            </div>
          </DialogHeader>

          <div className="py-6 space-y-5">
            {activeTab === 'operation' && (
              <p className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/20 rounded-lg p-3" data-testid="import-provider-hint">
                Data operation wajib ada kolum <b>SIGNAL PROVIDER</b> (Ciko, Arep, Fira, Syuk atau G). Jika ada baris tanpa provider yang sah, import dibatalkan dan tiada data disimpan.
              </p>
            )}
            {/* File Upload Area */}
            <div
              className={`border-2 border-dashed rounded-xl p-6 text-center transition-all ${
                importFile 
                  ? 'border-[#10B981] bg-[#10B981]/5' 
                  : 'border-[hsl(var(--border))] hover:border-[#10B981]/50 hover:bg-[#10B981]/5'
              }`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const file = e.dataTransfer.files[0];
                if (file && (file.name.endsWith('.csv') || file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))) {
                  setImportFile(file);
                } else {
                  toast.error('Hanya fail CSV atau Excel (.xlsx) dibenarkan');
                }
              }}
            >
              {importFile ? (
                <div className="flex items-center justify-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-[#10B981]/20 flex items-center justify-center">
                    <FileSpreadsheet size={24} className="text-[#10B981]" />
                  </div>
                  <div className="text-left">
                    <p className="text-white font-semibold">{importFile.name}</p>
                    <p className="text-xs text-[#6B7280]">{(importFile.size / 1024).toFixed(1)} KB</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-[#E63946] hover:bg-[#E63946]/10"
                    onClick={() => setImportFile(null)}
                  >
                    <X size={16} />
                  </Button>
                </div>
              ) : (
                <>
                  <div className="w-16 h-16 mx-auto mb-3 rounded-xl bg-[hsl(var(--surface-elevated))] flex items-center justify-center">
                    <Upload size={28} className="text-[#6B7280]" />
                  </div>
                  <p className="text-white font-medium mb-1">Drag & drop fail di sini</p>
                  <p className="text-xs text-[#6B7280] mb-3">atau</p>
                  <input
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    className="hidden"
                    id="import-file-input"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) setImportFile(file);
                    }}
                  />
                  <label htmlFor="import-file-input">
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-[hsl(var(--border))] text-white cursor-pointer"
                      asChild
                    >
                      <span>
                        <FileSpreadsheet size={14} className="mr-2" />
                        Pilih Fail
                      </span>
                    </Button>
                  </label>
                  <p className="text-[10px] text-[#6B7280] mt-3">Format: CSV, Excel (.xlsx)</p>
                </>
              )}
            </div>

            {/* Info Box */}
            <div className="bg-[hsl(var(--surface-elevated))] rounded-xl p-4 border border-[hsl(var(--border))]">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-[#3B82F6]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <FileQuestion size={16} className="text-[#3B82F6]" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white mb-1">Panduan Format Fail</p>
                  <ul className="text-xs text-[#9CA3AF] space-y-1">
                    <li>• Column pertama mestilah <span className="text-[#10B981] font-mono">DATE</span></li>
                    <li>• Marketing: SPEND FB, SPEND TT, LPV, CTA, SUBS TG, dll</li>
                    <li>• Sales: NEW (M), PROSPECT (S), ACTIVE CLIENT OCTA, dll</li>
                    <li>• Operation: PAIR, POSITION, STATUS, PIPS, SIGNAL PROVIDER (wajib)</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Download Template */}
            <Button
              variant="outline"
              size="sm"
              className="w-full border-[hsl(var(--border))] text-white hover:bg-[#3B82F6]/10"
              onClick={handleDownloadTemplate}
            >
              <Download size={14} className="mr-2" />
              Download Template ({activeTab === 'marketing' ? 'Marketing' : activeTab === 'sales' ? 'Sales' : 'Operation'})
            </Button>

            {/* Progress indicator */}
            {importProgress && (
              <div className="flex items-center justify-center gap-2 py-2">
                <Loader2 size={16} className="animate-spin text-[#10B981]" />
                <span className="text-sm text-[#9CA3AF]">{importProgress}</span>
              </div>
            )}
          </div>

          <DialogFooter className="border-t border-[hsl(var(--border))] pt-4">
            <Button 
              variant="outline" 
              onClick={() => { setShowImportModal(false); setImportFile(null); }}
              className="border-[hsl(var(--border))] text-white"
            >
              Batal
            </Button>
            <Button
              onClick={handleImportFile}
              disabled={!importFile || isImporting}
              className="bg-gradient-to-r from-[#10B981] to-[#3B82F6] text-white disabled:opacity-50"
              data-testid="import-submit-btn"
            >
              {isImporting ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Upload size={16} className="mr-2" />
                  Import Data
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="sm:max-w-[425px] bg-[#0A0A0B] border-[hsl(var(--border))]">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Trash2 size={20} className="text-[#E63946]" />
              Padam Data?
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-[#9CA3AF]">
              Adakah anda pasti mahu memadam data ini?
            </p>
            {deleteTarget && (
              <div className="mt-4 p-3 bg-[#E63946]/10 border border-[#E63946]/30 rounded-lg">
                <p className="text-sm text-white">
                  <span className="font-medium">{deleteTarget.type?.charAt(0).toUpperCase() + deleteTarget.type?.slice(1)}</span> entry
                </p>
                <p className="text-xs text-[#9CA3AF] mt-1">
                  Tarikh: {deleteTarget.date}
                </p>
              </div>
            )}
            <p className="text-xs text-[#6B7280] mt-4">
              💡 Tip: Anda boleh &quot;Undo&quot; untuk memulihkan data yang terpadam.
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowDeleteConfirm(false)}
              className="border-[hsl(var(--border))] text-white"
            >
              Batal
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDeleteEntry}
              disabled={isDeleting}
              className="bg-[#E63946] hover:bg-[#E63946]/90"
            >
              {isDeleting ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" />
                  Memadam...
                </>
              ) : (
                <>
                  <Trash2 size={16} className="mr-2" />
                  Ya, Padam
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick Log from Template Modal */}
      <QuickLogTemplateModal
        open={showQuickLogModal}
        onClose={() => { setShowQuickLogModal(false); setPrefillQuickLogText(''); }}
        ib={ib}
        onSaved={() => { fetchOperationData(); }}
        initialText={prefillQuickLogText}
        autoParse={!!prefillQuickLogText}
      />

      {/* Sync & Select — auto-fetch Telegram messages, pick one, flow into
          Quick Log for review + save (no copy-paste needed). */}
      <SyncSelectModal
        open={showSyncSelectModal}
        onClose={() => setShowSyncSelectModal(false)}
        ib={ib}
        onPickMessage={(text) => {
          setPrefillQuickLogText(text || '');
          setShowSyncSelectModal(false);
          // Small delay so the transition between modals feels smooth
          setTimeout(() => setShowQuickLogModal(true), 150);
        }}
      />
    </>
  );
}
